import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { motion, AnimatePresence } from 'framer-motion';
import { AppShell3 } from '@/components/app-shell-3';
import { ToolHeader } from '@/components/ToolHeader';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@/components/ui/accordion';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress as ProgressBar } from '@/components/ui/progress';
import { Progress } from '@/components/application/progress-steps/progress-steps';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { supabase } from '@/lib/supabase';
import { useSpaceData } from '@/hooks/useSpaceData';
import { useAuth } from '@/contexts/AuthContext';
import {
  Bot,
  Send,
  Plus,
  Edit,
  Trash2,
  Wand2,
  MessageSquare,
  Sparkles,
  ChevronRight,
  Building,
  Users,
  FileText,
  AlertCircle,
  CheckCircle,
  Clock,
  Shield,
  Loader2,
  AlertTriangle,
  Save,
  RefreshCw,
  MapPin,
  Briefcase,
  FileCheck,
  XCircle,
  CheckCircle2,
  Info,
  ChevronDown,
  LayoutGrid,
  BookOpen,
  Download,
  ChevronLeft,
  List,
  PanelLeftOpen,
  PanelRightOpen,
  X,
  Check,
  ListChecks,
  UserPlus,
  Link,
  Upload,
  HardHat,
  Award
} from 'lucide-react';
import { getOpenAI } from '@/lib/openai';
import { RecommendationsPanel } from '@/components/compliance/RecommendationsPanel';
import { complianceService } from '@/lib/complianceService';
import { cn } from '@/lib/utils';
import MessageFormatter from '@/components/MessageFormatter';
import { toast } from 'sonner';
import { PageTransition } from '@/components/PageTransition';
import { ChatFlowsEmbed } from 'chatflows-embed';
import {
  registerComplianceSkills,
  sendInitialContext
} from './chatflows-skills';

const CompliancePage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user, userProfile, company } = useAuth();

  // Use the cached space data hook
  // Pass company.id to validate the space belongs to the user's current company
  const { space, spaceTools, loading: spaceLoading, accessDenied } = useSpaceData(projectId, userProfile?.id, company?.id);

  // Set page title
  usePageTitle(space?.name ? `${space.name} Compliance` : 'Compliance');

  // Redirect if user doesn't have access (e.g., switched companies)
  useEffect(() => {
    if (accessDenied) {
      toast.error('You no longer have access to this space');
      navigate('/dashboard', { replace: true });
    }
  }, [accessDenied, navigate]);

  const [wizardStep, setWizardStep] = useState(1);
  const [wizardData, setWizardData] = useState({
    siteName: '',
    industry: '',
    teamSize: '',
    location: '',
    projectDescription: '',
    specialRequirements: ''
  });
  const [qualifications, setQualifications] = useState([]);
  const [qualificationsKey, setQualificationsKey] = useState(0); // Force re-render key
  const [messages, setMessages] = useState([]);
  const [userMessage, setUserMessage] = useState('');
  const [wizardMessage, setWizardMessage] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingRecs, setIsGeneratingRecs] = useState(false);
  const [isChatting, setIsChatting] = useState(false);
  // Type filter locked to qualifications only (checklists/docs moved to Site Compliance)
  const typeFilter = 'course';
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [recommendations, setRecommendations] = useState(null);
  const [riskAssessment, setRiskAssessment] = useState(null);
  const [selectedRequirement, setSelectedRequirement] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);
  const [editingRequirement, setEditingRequirement] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [teamMembers, setTeamMembers] = useState([]);
  const [showAssignmentDialog, setShowAssignmentDialog] = useState(false);
  const [assignmentPreview, setAssignmentPreview] = useState([]);
  const [selectedRequirementForAssignment, setSelectedRequirementForAssignment] = useState(null);
  const [isGeneratingAssignments, setIsGeneratingAssignments] = useState(false);
  const [assignmentRecommendations, setAssignmentRecommendations] = useState(null);
  const [currentAssignments, setCurrentAssignments] = useState([]);
  const [recommendationMembers, setRecommendationMembers] = useState([]); // Members used for current recommendations
  const [companyWideTrainedMembers, setCompanyWideTrainedMembers] = useState([]); // Company-wide trained members
  const [companyWideAllMembers, setCompanyWideAllMembers] = useState([]); // All company members not in space
  const [memberQualifications, setMemberQualifications] = useState({}); // Map of userId -> qualifications array

  // Wizard mode state
  const [wizardMode, setWizardMode] = useState(true);

  // Direct table column state (no collectedData wrapper)
  const [companyName, setCompanyName] = useState(null);
  const [industry, setIndustry] = useState(null);
  const [location, setLocation] = useState(null);
  const [teamSize, setTeamSize] = useState(null);
  const [equipment, setEquipment] = useState(null);
  const [risks, setRisks] = useState(null);
  const [projectDescription, setProjectDescription] = useState(null);
  const [specialRequirements, setSpecialRequirements] = useState(null);
  // Additional wizard fields for ChatFlows skills
  const [workSetting, setWorkSetting] = useState(null);
  const [workerRoles, setWorkerRoles] = useState(null);
  const [hazardousSubstances, setHazardousSubstances] = useState(null);
  const [environment, setEnvironment] = useState(null);
  const [heights, setHeights] = useState(null);
  const [incidentHistoryFileId, setIncidentHistoryFileId] = useState(null);
  const [accreditations, setAccreditations] = useState(null);

  // Generation options - locked to qualifications only (checklists/docs moved to Site Compliance)
  const generateQualifications = true;
  const generateChecklists = false;
  const generateDocuments = false;


  // Legacy wizardState object for backward compatibility (not actively used with ChatFlows)
  const wizardState = {
    collectedData: {
      company_name: companyName,
      industry: industry,
      location: location,
      team_size: teamSize,
      equipment: equipment,
      risks: risks,
      project_description: projectDescription,
      special_requirements: specialRequirements
    },
    sectionSummaries: {},
    expandedContext: {},
    currentSection: null
  };
  // Removed inline editing states - now using text areas
  const [suggestedResponses, setSuggestedResponses] = useState([]);
  const [showRegenerateButton, setShowRegenerateButton] = useState(false);
  const [showClearConfirmDialog, setShowClearConfirmDialog] = useState(false);
  const [deleteRequirementId, setDeleteRequirementId] = useState(null);

  // Panel visibility states
  const [showWizardPanel, setShowWizardPanel] = useState(false);
  const [showRequirementsPanel, setShowRequirementsPanel] = useState(false);

  // Refs for auto-scrolling
  const chatEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const chatInputRef = useRef(null);
  const chatFlowsRef = useRef(null);

  // Safety net: Track which requirements have been processed this session
  // Prevents infinite loops even if Supabase status updates fail
  const processedRequirementsRef = useRef(new Set());

  // Generation progress state for dynamic loading
  const [generationProgress, setGenerationProgress] = useState({
    stage: 0,
    message: 'Initializing AI analysis...',
    substeps: []
  });

  // Load existing compliance data on mount
  useEffect(() => {
    const initializeData = async () => {
      await fetchTeamMembers(); // Load team members
      await loadComplianceData(space); // Pass space data to loadComplianceData
    };
    if (space) {
      initializeData();
    }
  }, [projectId, space]);

  // Realtime subscription to listen for recommendation status changes
  useEffect(() => {
    if (!projectId) return;

    console.log('📡 Setting up realtime subscription for compliance_requirements');

    const channel = supabase
      .channel(`compliance-recommendations-${projectId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'compliance_requirements',
          filter: `project_id=eq.${projectId}`
        },
        (payload) => {
          console.log('📡 Realtime update received:', payload.new?.name, payload.new?.recommendations_status);

          // Update the specific qualification in state
          setQualifications(prev => prev.map(q =>
            q.id === payload.new.id ? { ...q, ...payload.new } : q
          ));
        }
      )
      .subscribe((status) => {
        console.log('📡 Realtime subscription status:', status);
      });

    return () => {
      console.log('📡 Cleaning up realtime subscription');
      supabase.removeChannel(channel);
    };
  }, [projectId]);

  // Cleanup on unmount - clear session when leaving page
  useEffect(() => {
    return () => {
      if (chatFlowsRef.current?.clearSession) {
        console.log('🧹 Cleaning up ChatFlows session on page exit');
        chatFlowsRef.current.clearSession();
      }
    };
  }, []);

  // Fetch team members for context
  const fetchTeamMembers = async () => {
    try {
      const { data, error } = await supabase
        .from('space_members')
        .select(
          `
          *,
          user_profiles!user_id (
            id,
            first_name,
            last_name,
            email,
            avatar_url,
            job_title
          )
        `
        )
        .eq('project_id', projectId)
        .order('created_date', { ascending: false });

      if (error) {
        console.error('Error fetching team members:', error);
        setTeamMembers([]);
      } else {
        // Transform to match expected structure
        const formattedMembers = (data || []).map(sm => ({
          id: sm.id, // space_members.id (used in compliance_assignments.team_member_id)
          space_member_id: sm.id, // Explicit space_members.id
          user_id: sm.user_id, // user_profiles.id
          name:
            `${sm.user_profiles.first_name || ''} ${
              sm.user_profiles.last_name || ''
            }`.trim() || sm.user_profiles.email,
          email: sm.user_profiles.email,
          role: sm.user_profiles.job_title,
          department: sm.department,
          status: sm.status,
          avatar_url: sm.user_profiles.avatar_url,
          compliance_percentage: sm.compliance_percentage
        }));

        setTeamMembers(formattedMembers);
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
      setTeamMembers([]);
    }
  };

  // Debug: Log qualifications changes
  useEffect(() => {
    console.log('🔍 Qualifications state changed:', qualifications.length, 'items');
    console.log('🔍 isGenerating:', isGenerating);
  }, [qualifications, isGenerating]);

  // Debug: Log generation toggle state changes
  useEffect(() => {
    console.log('🎚️ Toggle states updated:', {
      generateQualifications,
      generateChecklists,
      generateDocuments
    });
  }, [generateQualifications, generateChecklists, generateDocuments]);

  // Auto-save when qualifications change
  useEffect(() => {
    if (qualifications.length > 0 && !isLoading) {
      const timer = setTimeout(() => {
        saveComplianceData();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [qualifications]);

  // Helper function to get all embed data - always sends complete data (direct table columns)
  const getAllEmbedData = useCallback(() => {
    const embedData = {
      company_name: companyName,
      industry: industry,
      location: location,
      team_size: teamSize ?? teamMembers?.length ?? 0,
      equipment: equipment,
      risks: risks,
      project_description: projectDescription,
      special_requirements: specialRequirements,
      work_setting: workSetting,
      worker_roles: workerRoles,
      hazardous_substances: hazardousSubstances,
      environment: environment,
      heights: heights,
      incident_history_file_id: incidentHistoryFileId,
      accreditations: accreditations,
      teamMembers: teamMembers
    };

    // Include generated_requirements if qualifications exist
    if (qualifications && qualifications.length > 0) {
      embedData.generated_requirements = qualifications;
    }

    return embedData;
  }, [
    companyName,
    industry,
    location,
    teamSize,
    equipment,
    risks,
    projectDescription,
    specialRequirements,
    workSetting,
    workerRoles,
    hazardousSubstances,
    environment,
    heights,
    incidentHistoryFileId,
    accreditations,
    teamMembers,
    qualifications
  ]);

  // Auto-save wizard data when it changes (direct table columns)
  useEffect(() => {
    if (
      !isLoading &&
      (companyName ||
        industry ||
        location ||
        teamSize ||
        equipment ||
        risks ||
        projectDescription ||
        specialRequirements ||
        workSetting ||
        workerRoles ||
        hazardousSubstances ||
        environment ||
        heights ||
        incidentHistoryFileId ||
        accreditations)
    ) {
      const timer = setTimeout(() => {
        complianceService.saveWizardData(projectId, {
          company_name: companyName,
          industry: industry,
          location: location,
          team_size: teamSize,
          equipment: equipment,
          risks: risks,
          project_description: projectDescription,
          special_requirements: specialRequirements,
          work_setting: workSetting,
          worker_roles: workerRoles,
          hazardous_substances: hazardousSubstances,
          environment: environment,
          heights: heights,
          incident_history_file_id: incidentHistoryFileId,
          accreditations: accreditations
        });
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [
    companyName,
    industry,
    location,
    teamSize,
    equipment,
    risks,
    projectDescription,
    specialRequirements,
    workSetting,
    workerRoles,
    hazardousSubstances,
    environment,
    heights,
    incidentHistoryFileId,
    accreditations,
    projectId,
    isLoading
  ]);

  // Set up window.ChatflowsData for initial session context
  useEffect(() => {
    window.ChatflowsData = () => {
      const configData = getAllEmbedData();
      console.log('📤 [ChatflowsData] Returning context:', configData);
      return configData;
    };

    return () => {
      delete window.ChatflowsData;
    };
  }, [getAllEmbedData]);

  // Update ChatFlows embed when configuration changes
  useEffect(() => {
    if (
      !isLoading &&
      chatFlowsRef.current &&
      (companyName ||
        industry ||
        location ||
        teamSize ||
        equipment ||
        risks ||
        projectDescription ||
        specialRequirements ||
        workSetting ||
        workerRoles ||
        hazardousSubstances ||
        environment ||
        heights ||
        incidentHistoryFileId ||
        accreditations)
    ) {
      chatFlowsRef.current.update(getAllEmbedData());
    }
  }, [
    companyName,
    industry,
    location,
    teamSize,
    equipment,
    risks,
    projectDescription,
    specialRequirements,
    workSetting,
    workerRoles,
    hazardousSubstances,
    environment,
    heights,
    incidentHistoryFileId,
    accreditations,
    teamMembers,
    isLoading,
    getAllEmbedData
  ]);

  // Auto-scroll chat to bottom when new messages arrive
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Helper function to add a message (no longer saves to database - embed handles persistence)
  const addMessage = message => {
    // Validate message content
    if (!message.content || !message.content.trim()) {
      console.warn('Attempted to add empty message:', message);
      return;
    }

    setMessages(prev => [...prev, message]);
  };

  // Helper function to add multiple messages
  const addMessages = newMessages => {
    // Filter out empty messages
    const validMessages = newMessages.filter(
      msg => msg.content && msg.content.trim()
    );

    if (validMessages.length === 0) {
      console.warn('Attempted to add empty messages:', newMessages);
      return;
    }

    setMessages(prev => [...prev, ...validMessages]);
  };

  // Clear chat messages
  const clearChatMessages = async () => {
    setMessages([
      {
        role: 'assistant',
        content:
          '👋 Chat history cleared. How can I help you with compliance management today?'
      }
    ]);
    await complianceService.clearChatMessages(projectId);
  };

  // Reset all compliance data (for debugging)
  const resetComplianceData = async () => {
    setIsLoading(true);

    try {
      // Clear all compliance requirements
      await complianceService.deleteAllRequirements(projectId);

      // Clear chat messages
      await complianceService.clearChatMessages(projectId);

      // Clear wizard data from database
      const { error } = await supabase
        .from('compliance_wizard_data')
        .delete()
        .eq('project_id', projectId);

      if (error) {
        console.error('Error clearing wizard data:', error);
      }

      // Reset all local state to initial values
      setQualifications([]);
      setMessages([]);
      setUserMessage('');
      setWizardMode(true);
      setWizardData({
        siteName: '',
        industry: '',
        location: '',
        teamSize: '',
        projectDescription: '',
        specialRequirements: ''
      });
      // Reset to empty state (no sections, no nested objects)
      setWizardState({
        collectedData: {}
      });
      setRecommendations(null);
      setRiskAssessment(null);
      setEditingRequirement(null);

      // Reload fresh data (which will now be empty and start wizard)
      await loadComplianceData(space);
    } catch (error) {
      console.error('Error resetting compliance data:', error);
      alert('Failed to reset compliance data. Check console for errors.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadComplianceData = async (spaceData = null, { silent = false } = {}) => {
    if (!silent) {
      setIsLoading(true);
    }
    const currentSpace = spaceData || space; // Use passed space data or state

    // Load compliance data (chat is handled by embed now)
    const result = await complianceService.loadProjectCompliance(projectId);

    if (result.success) {
      if (result.requirements.length > 0) {
        setQualifications(result.requirements);
        // Auto-show requirements panel if there are requirements
        setShowRequirementsPanel(true);
      }

      // Load wizard data from database - set direct state variables
      // Note: Always set state even if value is null/empty string, so we don't skip falsy values
      if (result.wizardData) {
        setCompanyName(result.wizardData.company_name ?? null);
        setIndustry(result.wizardData.industry ?? null);
        setLocation(result.wizardData.location ?? null);
        setTeamSize(result.wizardData.team_size ?? null);
        setEquipment(result.wizardData.equipment ?? null);
        setRisks(result.wizardData.risks ?? null);
        setProjectDescription(result.wizardData.project_description ?? null);
        setSpecialRequirements(result.wizardData.special_requirements ?? null);
        // Load additional wizard fields
        setWorkSetting(result.wizardData.work_setting ?? null);
        setWorkerRoles(result.wizardData.worker_roles ?? null);
        setHazardousSubstances(result.wizardData.hazardous_substances ?? null);
        setEnvironment(result.wizardData.environment ?? null);
        setHeights(result.wizardData.heights ?? null);
        setIncidentHistoryFileId(result.wizardData.incident_history_file_id ?? null);
        setAccreditations(result.wizardData.accreditations ?? null);

        // Auto-show wizard panel if there's wizard data
        if (
          result.wizardData.company_name ||
          result.wizardData.industry ||
          result.wizardData.location ||
          result.wizardData.team_size ||
          result.wizardData.equipment ||
          result.wizardData.risks
        ) {
          setShowWizardPanel(true);
        }

        // If we have wizard data, don't show wizard mode initially
        if (result.wizardData.company_name || result.wizardData.industry) {
          setWizardMode(false);
        }
      }

      if (result.requirements.length === 0 && !result.wizardData) {
        // No wizard data and no requirements - start wizard with pre-populated data from space
        setWizardMode(true);

        // Pre-populate wizard data from space/project details if available
        if (currentSpace) {
          if (currentSpace.name) setCompanyName(currentSpace.name);
          if (currentSpace.industry) setIndustry(currentSpace.industry);
          if (currentSpace.location) setLocation(currentSpace.location);
          if (currentSpace.description)
            setProjectDescription(currentSpace.description);
        }
      }

      if (result.recommendations.length > 0) {
        const recs = {
          immediate: result.recommendations
            .filter(r => r.type === 'immediate')
            .map(r => r.recommendation),
          shortTerm: result.recommendations
            .filter(r => r.type === 'short_term')
            .map(r => r.recommendation),
          longTerm: result.recommendations
            .filter(r => r.type === 'long_term')
            .map(r => r.recommendation)
        };
        setRecommendations(recs);
      }

      if (result.riskAssessment) {
        setRiskAssessment({
          level: result.riskAssessment.risk_level,
          factors: result.riskAssessment.risk_factors || [],
          mitigation: result.riskAssessment.mitigation_strategies || [],
          nonComplianceConsequences:
            result.riskAssessment.non_compliance_consequences || []
        });
      }

      // Send initial configuration to ChatFlows embed on load
      if (chatFlowsRef.current && result.wizardData) {
        // Always send all data
        chatFlowsRef.current.update(getAllEmbedData());
      }
    }

    setIsLoading(false);
  };

  const saveComplianceData = async () => {
    setIsSaving(true);

    // Save wizard data
    if (wizardData.siteName) {
      await complianceService.saveWizardData(projectId, {
        site_name: wizardData.siteName,
        industry: wizardData.industry,
        location: wizardData.location,
        team_size: parseInt(wizardData.teamSize) || null,
        project_description: wizardData.projectDescription,
        special_requirements: wizardData.specialRequirements
      });

      // Update ChatFlows embed with configuration changes
      if (chatFlowsRef.current) {
        // Always send all data
        chatFlowsRef.current.update(getAllEmbedData());
      }
    }

    // Save recommendations
    if (recommendations) {
      await complianceService.saveRecommendations(projectId, recommendations);
    }

    // Save risk assessment
    if (riskAssessment) {
      await complianceService.saveRiskAssessment(projectId, riskAssessment);
    }

    setLastSaved(new Date());
    setIsSaving(false);
  };

  const handleWizardSubmit = useCallback(async (projectDataOverride = null) => {
    console.log('🚀 Starting handleWizardSubmit...');
    setIsGenerating(true);
    setWizardMessage('Analyzing your requirements with AI...');

    try {
      // Prepare comprehensive context for N8N
      const dataToUse = projectDataOverride || {
        ...wizardData,
        teamMembers: teamMembers || []
      };

      const contextPayload = {
        projectId: projectId,
        companyId: space?.company_id || company?.id,
        companyName: company?.name,

        // Space/Project Details
        space: {
          id: projectId,
          name: space?.name,
          type: space?.type,
          industry: space?.industry,
          location: space?.location,
          description: space?.description
        },

        // Collected wizard data (direct table columns)
        collectedData: {
          company_name: companyName,
          industry: industry,
          location: location,
          team_size: teamSize,
          equipment: equipment,
          risks: risks,
          project_description: projectDescription,
          special_requirements: specialRequirements,
          work_setting: workSetting,
          worker_roles: workerRoles,
          hazardous_substances: hazardousSubstances,
          environment: environment,
          heights: heights,
          incident_history_file_id: incidentHistoryFileId,
          accreditations: accreditations
        },

        // Generation options
        generationOptions: {
          generateQualifications: generateQualifications,
          generateChecklists: generateChecklists,
          generateDocuments: generateDocuments
        },

        // Section summaries from AI (empty for now - not used with ChatFlows)
        sectionSummaries: {},

        // Expanded context (empty for now - not used with ChatFlows)
        expandedContext: {},

        // Team member data
        teamMembers: teamMembers || [],
        teamSize: dataToUse.teamSize || teamMembers?.length || 0,

        // Flattened project data for backward compatibility
        projectData: dataToUse,

        // Chat conversation history (if needed)
        chatHistory: messages || [],

        // User context
        user: {
          id: user?.id,
          email: user?.email,
          name: userProfile?.full_name || user?.email,
          role: userProfile?.role
        }
      };

      console.log('🔄 Generation toggle states:', {
        generateQualifications,
        generateChecklists,
        generateDocuments
      });
      console.log('🚀 Sending context to N8N:', contextPayload);

      // Call N8N webhook and wait for response
      const response = await fetch(
        'https://n8n.add.ai/webhook/generate-compliance-results',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(contextPayload)
        }
      );

      console.log('📡 N8N webhook response status:', response.status);

      if (!response.ok) {
        throw new Error(
          `N8N webhook failed: ${response.status} ${response.statusText}`
        );
      }

      const result = await response.json();
      console.log('✅ N8N webhook completed');

      // N8N has already saved everything to the database
      // Just fetch all compliance data from database
      console.log('📥 Fetching all compliance requirements from database...');
      const loadResult = await complianceService.loadProjectCompliance(projectId);

      if (loadResult.success && loadResult.requirements) {
        console.log('✅ Loaded', loadResult.requirements.length, 'requirements from database');
        console.log('📊 Requirements data:', loadResult.requirements);
        setQualifications(loadResult.requirements);
        console.log('✅ setQualifications called with', loadResult.requirements.length, 'items');

        // Also update recommendations and risk assessment if returned
        if (loadResult.recommendations && loadResult.recommendations.length > 0) {
          const recs = {
            immediate: loadResult.recommendations
              .filter(r => r.type === 'immediate')
              .map(r => r.recommendation),
            shortTerm: loadResult.recommendations
              .filter(r => r.type === 'short_term')
              .map(r => r.recommendation),
            longTerm: loadResult.recommendations
              .filter(r => r.type === 'long_term')
              .map(r => r.recommendation)
          };
          setRecommendations(recs);
        }

        if (loadResult.riskAssessment) {
          setRiskAssessment({
            level: loadResult.riskAssessment.risk_level,
            factors: loadResult.riskAssessment.risk_factors || [],
            mitigation: loadResult.riskAssessment.mitigation_strategies || [],
            nonComplianceConsequences:
              loadResult.riskAssessment.non_compliance_consequences || []
          });
        }

        // Show requirements panel
        setShowRequirementsPanel(true);

        const count = loadResult.requirements.length;
        const successMessage = `✅ Successfully generated ${count} compliance requirements for your team.`;
        setWizardMessage(successMessage);

        // Show success toast
        toast.success(`Generated ${count} compliance requirements`, {
          description: 'Requirements have been saved and are now visible in the preview panel'
        });

        console.log('✅ All data loaded and displayed successfully');

        // Auto-trigger recommendation generation for course-type requirements
        // Small delay to ensure state is updated
        setTimeout(() => {
          console.log('🔄 Auto-triggering recommendation generation...');
          handleRefreshRecommendations();
        }, 500);
      } else {
        console.error('❌ Failed to load requirements from database');
        toast.error('Failed to load requirements', {
          description: 'Unable to fetch requirements from database. Please refresh the page.'
        });
      }
    } catch (error) {
      const errorMessage = '❌ An error occurred while generating requirements.';
      setWizardMessage(errorMessage);
      console.error('❌ Error calling N8N:', error);

      // Show error toast with details
      toast.error('Generation failed', {
        description: error.message || 'Unable to connect to the AI service. Please check your connection and try again.'
      });
    } finally {
      console.log('🏁 Finished handleWizardSubmit, stopping loader...');
      setIsGenerating(false);
      console.log('🏁 isGenerating set to false');
    }
  }, [
    projectId,
    space,
    company,
    teamMembers,
    companyName,
    industry,
    location,
    teamSize,
    equipment,
    risks,
    projectDescription,
    specialRequirements,
    workSetting,
    workerRoles,
    hazardousSubstances,
    environment,
    heights,
    incidentHistoryFileId,
    accreditations,
    user,
    userProfile,
    complianceService,
    generateQualifications,
    generateChecklists,
    generateDocuments
  ]);

  const generateRequirementsFromWizard = useCallback(async () => {
    // Use direct state variables for requirement generation
    const projectData = {
      siteName: companyName || '',
      industry: industry || '',
      location: location || '',
      teamSize: teamSize || teamMembers?.length || '',
      roleBreakdown: null,
      projectDescription: projectDescription || '',
      specialRequirements: [
        equipment || '',
        risks || '',
        specialRequirements || ''
      ]
        .filter(Boolean)
        .join('. '),
      teamMembers: teamMembers || [],
      // Additional wizard fields
      workSetting: workSetting || '',
      workerRoles: workerRoles || null,
      hazardousSubstances: hazardousSubstances || '',
      environment: environment || '',
      heights: heights || '',
      incidentHistoryFileId: incidentHistoryFileId || null,
      accreditations: accreditations || ''
    };

    // Update wizard data state
    setWizardData(projectData);

    // Call the wizard submit with the collected data
    await handleWizardSubmit(projectData);

    // Update ChatFlows embed with new configuration - always send all data
    if (chatFlowsRef.current) {
      chatFlowsRef.current.update(getAllEmbedData());
    }

    setWizardMode(false); // Switch chat to management mode
    await addMessage({
      role: 'assistant',
      content:
        "🎉 Great! I've generated your compliance requirements based on the information you provided. You can now manage these requirements - add, edit, or remove items as needed. Just tell me what you'd like to do!"
    });
  }, [
    companyName,
    industry,
    location,
    teamSize,
    teamMembers,
    projectDescription,
    equipment,
    risks,
    specialRequirements,
    workSetting,
    workerRoles,
    hazardousSubstances,
    environment,
    heights,
    incidentHistoryFileId,
    accreditations,
    handleWizardSubmit,
    getAllEmbedData,
    addMessage
  ]);

  // Re-register skills when toggle states change
  useEffect(() => {
    if (!chatFlowsRef.current || !projectId) {
      return;
    }

    console.log('🔄 Re-registering skills due to toggle change...');

    const skillContext = {
      setCompanyName,
      setIndustry,
      setLocation,
      setTeamSize,
      setEquipment,
      setRisks,
      setProjectDescription,
      setSpecialRequirements,
      setWorkSetting,
      setWorkerRoles,
      setHazardousSubstances,
      setEnvironment,
      setHeights,
      setIncidentHistoryFileId,
      setAccreditations,
      companyName,
      industry,
      location,
      teamSize,
      equipment,
      risks,
      projectDescription,
      specialRequirements,
      workSetting,
      workerRoles,
      hazardousSubstances,
      environment,
      heights,
      incidentHistoryFileId,
      accreditations,
      setIsGenerating: setIsSaving,
      projectId,
      teamMembers,
      qualifications,
      setQualifications,
      complianceService,
      generateRequirementsFromWizard,
      getAllEmbedData
    };

    try {
      registerComplianceSkills(chatFlowsRef, skillContext);
      console.log('✅ Skills re-registered with new toggle values:', {
        generateQualifications,
        generateChecklists,
        generateDocuments
      });
    } catch (error) {
      console.error('❌ Error re-registering skills:', error);
    }
  }, [
    generateQualifications,
    generateChecklists,
    generateDocuments,
    generateRequirementsFromWizard,
    projectId,
    companyName,
    industry,
    location,
    teamSize,
    equipment,
    risks,
    projectDescription,
    specialRequirements,
    workSetting,
    workerRoles,
    hazardousSubstances,
    environment,
    heights,
    incidentHistoryFileId,
    accreditations,
    teamMembers,
    qualifications,
    complianceService,
    getAllEmbedData
  ]);

  // Handler for when ChatFlows embed is ready
  const handleChatFlowsReady = useCallback(() => {
    console.log(
      '🚀 [TEAM COMPLIANCE] ChatFlows embed ready - checking conditions...'
    );
    console.log('📍 [TEAM COMPLIANCE] Call stack:', new Error().stack);

    if (chatFlowsRef.current && space) {
      console.log('✅ [TEAM COMPLIANCE] All conditions met - initializing...');

      // Longer delay to ensure iframe JavaScript is ready to receive messages
      setTimeout(() => {
        // 0. Clear any existing session first
        console.log(
          '🧹 [TEAM COMPLIANCE] Clearing existing session for space:',
          projectId
        );
        if (chatFlowsRef.current?.clearSession) {
          chatFlowsRef.current.clearSession();
        }

        // Wait a bit for session to clear before initializing new one
        setTimeout(() => {
          // Get all embed data (same structure as embed_update)
          const embedData = getAllEmbedData();

          console.log(
            '🔄 [TEAM COMPLIANCE] Initializing ChatFlows session with embed data'
          );
          console.log('📦 [TEAM COMPLIANCE] Embed data:', embedData);

          // Send session_update to reinitialize session with space as user
          // Find the ChatFlows iframe (works for both localhost and production)
          const iframe =
            document.querySelector('iframe[src*="/embed/"]') ||
            document.querySelector('iframe[src*="chatflows"]') ||
            document.querySelector('iframe');

          console.log('🔍 [TEAM COMPLIANCE] Found iframe:', iframe?.src);

          if (iframe?.contentWindow) {
            console.log(
              '📤 [TEAM COMPLIANCE] Sending embed:session_update message'
            );
            iframe.contentWindow.postMessage(
              {
                type: 'embed:session_update',
                user: {
                  id: `${projectId}-team`,
                  metadata: {
                    embedData
                  }
                }
              },
              '*'
            );
            console.log(
              '✅ [TEAM COMPLIANCE] embed:session_update message sent with data'
            );
          } else {
            console.error('❌ [TEAM COMPLIANCE] No iframe found!');
          }

          // 2. Register skills (after session initialized)
          setTimeout(() => {
            console.log('🔧 [TEAM COMPLIANCE] Registering skills...');
            console.log(
              '🔧 [TEAM COMPLIANCE] chatFlowsRef.current:',
              chatFlowsRef.current
            );
            console.log(
              '🔧 [TEAM COMPLIANCE] registerSkill exists?:',
              typeof chatFlowsRef.current?.registerSkill
            );

            const skillContext = {
              // Direct state setters (no collectedData wrapper)
              setCompanyName,
              setIndustry,
              setLocation,
              setTeamSize,
              setEquipment,
              setRisks,
              setProjectDescription,
              setSpecialRequirements,
              setWorkSetting,
              setWorkerRoles,
              setHazardousSubstances,
              setEnvironment,
              setHeights,
              setIncidentHistoryFileId,
              setAccreditations,
              // Current state values for embed updates
              companyName,
              industry,
              location,
              teamSize,
              equipment,
              risks,
              projectDescription,
              specialRequirements,
              workSetting,
              workerRoles,
              hazardousSubstances,
              environment,
              heights,
              incidentHistoryFileId,
              accreditations,
              setIsGenerating: setIsSaving,
              projectId,
              teamMembers,
              qualifications,
              setQualifications,
              complianceService,
              generateRequirementsFromWizard,
              getAllEmbedData
            };

            try {
              registerComplianceSkills(chatFlowsRef, skillContext);
              console.log(
                '✅ [TEAM COMPLIANCE] ChatFlows compliance skills registered'
              );
            } catch (error) {
              console.error(
                '❌ [TEAM COMPLIANCE] Error registering skills:',
                error
              );
            }

            // 3. Send initial context (with retry mechanism)
            // Send immediately
            sendInitialContext(chatFlowsRef, { getAllEmbedData });

            // Send again after 500ms to ensure iframe caught it
            setTimeout(() => {
              sendInitialContext(chatFlowsRef, { getAllEmbedData });
            }, 500);

            // Send again after 1s as final retry
            setTimeout(() => {
              sendInitialContext(chatFlowsRef, { getAllEmbedData });
            }, 1000);
          }, 300); // Wait for session to initialize
        }, 200); // Wait 200ms after clearing
      }, 300); // Longer initial delay
    } else {
      console.warn('❌ Conditions not met for initialization:', {
        hasChatFlowsRef: !!chatFlowsRef.current,
        hasSpace: !!space
      });
    }
  }, [
    space,
    teamMembers,
    companyName,
    industry,
    location,
    teamSize,
    equipment,
    risks,
    projectDescription,
    specialRequirements,
    workSetting,
    workerRoles,
    hazardousSubstances,
    environment,
    heights,
    incidentHistoryFileId,
    accreditations,
    qualifications,
    projectId,
    complianceService,
    generateRequirementsFromWizard,
    setIsSaving,
    company,
    user,
    userProfile,
    getAllEmbedData,
    generateQualifications,
    generateChecklists,
    generateDocuments
  ]);


  const handleChatSubmit = async (messageOverride = null) => {
    const messageToSend = messageOverride || userMessage;
    if (messageToSend.trim()) {
      const openAI = getOpenAI();

      if (!openAI) {
        await addMessages([
          { role: 'user', content: messageToSend },
          {
            role: 'assistant',
            content:
              'Please configure your OpenAI API key to use the chat assistant.'
          }
        ]);
        setUserMessage('');
        // Refocus the input
        setTimeout(() => chatInputRef.current?.focus(), 0);
        return;
      }

      const userMsg = { role: 'user', content: messageToSend };
      await addMessage(userMsg);
      const newMessages = [...messages, userMsg];
      setUserMessage('');
      // Refocus the input
      setTimeout(() => chatInputRef.current?.focus(), 0);
      setIsChatting(true);

      try {
        // Send to AI first to collect the data, then check for section completion
        // This ensures the AI processes the user's response before we move forward
        let result;

        if (wizardMode) {
          // Use wizard chat for collecting information (legacy - not used with ChatFlows)
          const wizardStateForChat = {
            collectedData: {
              company_name: companyName,
              industry: industry,
              location: location,
              team_size: teamSize,
              equipment: equipment,
              risks: risks,
              project_description: projectDescription,
              special_requirements: specialRequirements
            },
            sectionSummaries: {},
            expandedContext: {}
          };
          result = await openAI.wizardChat(newMessages, wizardStateForChat);
        } else {
          // Use regular chat for managing existing requirements
          const context = {
            currentQualifications: qualifications,
            projectInfo: wizardData,
            recommendations: recommendations,
            riskAssessment: riskAssessment
          };
          result = await openAI.chatWithAssistant(newMessages, context);
        }

        if (result.success) {
          const aiResponse = result.response;

          console.log('AI Response:', aiResponse); // Debug logging

          // Process ONLY the primary action - ignore conflicting actions
          let messageContent = '';
          let shouldUpdateState = false;
          let stateUpdate = {};

          if (wizardMode) {
            // Wizard mode - simplified handling with priority order
            // Process section_complete first (highest priority)
            if (aiResponse.action === 'wizard_section_complete') {
              // Map section names from AI to internal names
              const sectionMapping = {
                siteDetails: 'about',
                teamSize: 'workforce',
                risk: 'risk',
                teamVerification: 'teamVerification'
              };

              const internalSection =
                sectionMapping[aiResponse.section] || aiResponse.section;
              const nextSection =
                aiResponse.next_section === 'generate'
                  ? 'confirmation'
                  : aiResponse.next_section;
              const internalNextSection =
                sectionMapping[nextSection] || nextSection;

              console.log('Section completed:', internalSection);

              shouldUpdateState = true;
              stateUpdate = {
                ...wizardState
              };

              messageContent = `✅ ${
                aiResponse.summary || 'Section completed.'
              }\n\n`;

              // Add next section prompt - use internalNextSection which has been mapped
              if (internalNextSection === 'workforce') {
                // Check if we have team context
                if (teamMembers && teamMembers.length > 0) {
                  const departments = [
                    ...new Set(
                      teamMembers.map(m => m.department).filter(Boolean)
                    )
                  ];
                  messageContent += `Now let's talk about your workforce. I can see you have **${teamMembers.length} staff members** registered in your team management system`;
                  if (departments.length > 0) {
                    messageContent += ` across these departments: ${departments.join(
                      ', '
                    )}`;
                  }
                  messageContent += `.\n\nIs this the correct total number of staff for your ${
                    space?.name || 'site'
                  }? If not, just let me know the actual number of employees.`;

                  setSuggestedResponses([
                    {
                      text: `✅ Yes, ${teamMembers.length} is correct`,
                      value: `Yes, we have ${teamMembers.length} employees`
                    },
                    {
                      text: 'Actually less',
                      value: 'We actually have fewer employees than that'
                    },
                    {
                      text: 'Actually more',
                      value: 'We actually have more employees than that'
                    },
                    {
                      text: 'Different number',
                      value: 'Let me give you the correct number'
                    }
                  ]);
                } else {
                  messageContent +=
                    "Now let's move on to understanding your workforce. How many people work at your " +
                    (space?.name || 'site') +
                    '?\n\n💡 **Tip**: You can add team members to the system later through the Team Management tool.';
                  setSuggestedResponses([
                    {
                      text: 'Under 10',
                      value: 'We have less than 10 employees'
                    },
                    {
                      text: '10-50',
                      value: 'We have between 10 and 50 employees'
                    },
                    {
                      text: '50-100',
                      value: 'We have between 50 and 100 employees'
                    },
                    { text: 'Over 100', value: 'We have over 100 employees' }
                  ]);
                }
              } else if (internalNextSection === 'risk') {
                // Contextual suggestions based on industry and what we know
                const isWarehouse =
                  space?.industry?.toLowerCase().includes('warehouse') ||
                  space?.industry?.toLowerCase().includes('distribution') ||
                  space?.name?.toLowerCase().includes('warehouse');

                const hasChemicals =
                  space?.description?.toLowerCase().includes('chemical') ||
                  space?.description?.toLowerCase().includes('bleach') ||
                  space?.description?.toLowerCase().includes('acid') ||
                  wizardState.collectedData?.about?.description
                    ?.toLowerCase()
                    .includes('chemical') ||
                  wizardState.collectedData?.about?.description
                    ?.toLowerCase()
                    .includes('hazardous');

                const hasForklifts = teamMembers?.some(
                  m =>
                    m.department?.toLowerCase().includes('forklift') ||
                    m.role?.toLowerCase().includes('forklift')
                );

                messageContent +=
                  "Excellent! Now let's assess the risks and environmental factors. What are the main safety hazards or risks in your workplace?";

                // Build contextual suggestions
                const suggestions = [];

                if (hasChemicals) {
                  suggestions.push({
                    text: '🧪 Chemical hazards',
                    value:
                      'We handle hazardous chemicals including acids and bleaches requiring COSHH controls'
                  });
                }

                if (isWarehouse || hasForklifts) {
                  suggestions.push({
                    text: '🏗️ Warehouse hazards',
                    value:
                      'Manual handling, forklift operations, working at height on racking, and slip/trip hazards'
                  });
                }

                if (hasForklifts) {
                  suggestions.push({
                    text: '🚜 Vehicle movements',
                    value:
                      'Forklift and vehicle movements in warehouse, pedestrian segregation required'
                  });
                }

                // Always include these options
                suggestions.push({
                  text: '⚠️ Multiple hazards',
                  value:
                    'Chemical handling, manual handling, forklift operations, and working at height'
                });

                if (suggestions.length === 0 || !isWarehouse) {
                  // Fallback to generic options if no context
                  suggestions.push(
                    {
                      text: 'Physical hazards',
                      value:
                        'We have physical hazards like machinery, heights, or manual handling'
                    },
                    {
                      text: 'Chemical hazards',
                      value: 'We work with chemicals or hazardous substances'
                    },
                    {
                      text: 'Environmental',
                      value:
                        'We have environmental risks like noise, temperature, or dust'
                    },
                    {
                      text: 'Low risk office',
                      value: "We're a low-risk office environment"
                    }
                  );
                }

                setSuggestedResponses(suggestions);
              } else if (internalNextSection === 'teamVerification') {
                // Team verification section - check if user corrected team size
                const correctedTeamSize =
                  wizardState.collectedData?.workforce?.teamSize;

                if (
                  correctedTeamSize &&
                  teamMembers &&
                  teamMembers.length > 0
                ) {
                  // User provided a different team size than what's in the system
                  const roleGroups = {};
                  teamMembers.forEach(member => {
                    const key = `${member.role} in ${
                      member.department || 'Unassigned'
                    }`;
                    roleGroups[key] = (roleGroups[key] || 0) + 1;
                  });

                  let teamSummary = `Perfect! You mentioned you have **${correctedTeamSize} employees** total.\n\n`;
                  teamSummary += `I can see ${teamMembers.length} registered in the system:\n`;
                  Object.entries(roleGroups).forEach(([role, count]) => {
                    teamSummary += `• ${count} ${role}\n`;
                  });

                  const difference =
                    parseInt(correctedTeamSize) - teamMembers.length;
                  if (difference > 0) {
                    teamSummary += `\nShould I plan compliance for all ${correctedTeamSize} employees, or would you like to specify the departments and roles for the additional ${difference} staff?`;

                    setSuggestedResponses([
                      {
                        text: `✅ Plan for all ${correctedTeamSize} employees`,
                        value: `Yes, plan for all ${correctedTeamSize} employees`
                      },
                      {
                        text: '📝 Specify other departments',
                        value:
                          'Let me specify the departments for the additional staff'
                      },
                      {
                        text: '✏️ Correct the numbers',
                        value: 'I need to correct these numbers'
                      }
                    ]);
                  } else {
                    teamSummary += '\nIs this correct?';
                    setSuggestedResponses([
                      {
                        text: "✅ Yes, that's correct",
                        value: 'Yes, the team structure is correct'
                      },
                      {
                        text: '✏️ No, let me correct',
                        value: 'The team structure needs correction'
                      }
                    ]);
                  }

                  messageContent += teamSummary;
                } else if (teamMembers && teamMembers.length > 0) {
                  // No corrected team size, show actual team members
                  const roleGroups = {};
                  teamMembers.forEach(member => {
                    const key = `${member.role} in ${
                      member.department || 'Unassigned'
                    }`;
                    roleGroups[key] = (roleGroups[key] || 0) + 1;
                  });

                  let teamSummary =
                    'Perfect! Let me verify your team structure:\n\n';
                  Object.entries(roleGroups).forEach(([role, count]) => {
                    teamSummary += `• ${count} ${role}\n`;
                  });
                  teamSummary += '\nIs this correct?';

                  messageContent += teamSummary;
                  setSuggestedResponses([
                    {
                      text: "✅ Yes, that's correct",
                      value: 'Yes, the team structure is correct'
                    },
                    {
                      text: '✏️ No, let me correct',
                      value: 'The team structure needs correction'
                    }
                  ]);
                } else {
                  messageContent +=
                    'Now let me verify your team details. How many employees do you have in total?';
                  setSuggestedResponses([
                    {
                      text: 'Under 10',
                      value: 'We have less than 10 employees'
                    },
                    {
                      text: '10-50',
                      value: 'We have between 10 and 50 employees'
                    },
                    {
                      text: '50-100',
                      value: 'We have between 50 and 100 employees'
                    },
                    { text: 'Over 100', value: 'We have over 100 employees' }
                  ]);
                }
              } else if (internalNextSection === 'confirmation') {
                messageContent +=
                  'Ready to generate requirements! Is all the information correct?';
                setSuggestedResponses([
                  {
                    text: '✅ Yes, generate requirements',
                    value: 'Yes, generate requirements'
                  },
                  {
                    text: '✏️ Change something',
                    value: 'I need to change some details'
                  }
                ]);
              }
            } else if (aiResponse.action === 'wizard_collect') {
              // Map section names from AI to internal names
              const sectionMapping = {
                siteDetails: 'about',
                teamSize: 'workforce',
                risk: 'risk',
                teamVerification: 'teamVerification'
              };

              // Use the section from AI response if provided, otherwise use current section
              const aiSection =
                aiResponse.section || wizardState.currentSection;
              const sectionToUse =
                sectionMapping[aiSection] || aiSection || 'about';
              const currentSectionData = {
                ...wizardState.collectedData[sectionToUse],
                [aiResponse.field]: aiResponse.value
              };

              // Build summary
              let summary = '';
              Object.entries(currentSectionData).forEach(([key, value]) => {
                if (value) {
                  const label = key
                    .replace(/([A-Z])/g, ' $1')
                    .replace(/_/g, ' ')
                    .trim();
                  summary += `${
                    label.charAt(0).toUpperCase() + label.slice(1)
                  }: ${value}\n`;
                }
              });

              shouldUpdateState = true;
              stateUpdate = {
                collectedData: {
                  ...wizardState.collectedData,
                  [sectionToUse]: currentSectionData
                },
                sectionSummaries: {
                  ...wizardState.sectionSummaries,
                  [sectionToUse]: summary.trim()
                },
                expandedContext: wizardState.expandedContext
              };

              messageContent =
                aiResponse.message || 'Thank you. What else can you tell me?';
            } else if (aiResponse.action === 'generate_requirements') {
              // Generate requirements
              await generateRequirementsFromWizard(aiResponse.collected_data);
              return; // Exit early as generateRequirementsFromWizard handles the message
            } else if (aiResponse.action === 'wizard_ask') {
              // AI is asking a question
              messageContent =
                aiResponse.question || 'Could you provide more details?';

              // Set suggested responses if provided
              if (aiResponse.suggestions && aiResponse.suggestions.length > 0) {
                setSuggestedResponses(
                  aiResponse.suggestions.map(s => ({
                    text: s,
                    value: s
                  }))
                );
              }
            } else if (aiResponse.action === 'wizard_confirm') {
              // AI is confirming collected data
              messageContent =
                aiResponse.message || 'Is all the information correct?';
              setSuggestedResponses([
                {
                  text: '✅ Yes, generate requirements',
                  value: 'Yes, generate requirements'
                },
                {
                  text: '✏️ Change something',
                  value: 'I need to change some details'
                }
              ]);
            } else {
              // Fallback - should not reach here with improved validation
              messageContent =
                'How can I help you with your compliance requirements?';
            }
          } else {
            // Management mode - handle requirement operations
            if (aiResponse.action === 'add') {
              // Add new qualification to database
              const saveResult = await complianceService.addRequirement(
                projectId,
                {
                  name: aiResponse.qualification.name,
                  description: aiResponse.qualification.description,
                  category: aiResponse.qualification.category,
                  priority: aiResponse.qualification.priority,
                  renewal_period: aiResponse.qualification.renewalPeriod,
                  status: 'pending',
                  regulatory_body:
                    aiResponse.qualification.regulatoryBody || '',
                  notes: aiResponse.qualification.notes || '',
                  minimum_personnel:
                    aiResponse.qualification.minimumPersonnel || null,
                  calculated_required:
                    aiResponse.qualification.calculatedRequired || null
                }
              );

              if (saveResult.success) {
                setQualifications(prev => [...prev, saveResult.data]);
                await addMessage({
                  role: 'assistant',
                  content: `✅ Added: ${aiResponse.qualification.name}`
                });
              } else {
                await addMessage({
                  role: 'assistant',
                  content: `❌ Failed to add requirement: ${saveResult.error}`
                });
              }
            } else if (aiResponse.action === 'edit') {
              // Edit existing qualification
              const updateResult = await complianceService.updateRequirement(
                aiResponse.id,
                aiResponse.changes
              );

              if (updateResult.success) {
                setQualifications(prev =>
                  prev.map(qual =>
                    qual.id === aiResponse.id ? updateResult.data : qual
                  )
                );
                await addMessage({
                  role: 'assistant',
                  content: `✅ Updated qualification`
                });
              }
            } else if (aiResponse.action === 'warn_delete') {
              // Warning before deleting a required qualification
              await addMessage({
                role: 'assistant',
                content: aiResponse.message
              });
            } else if (aiResponse.action === 'delete') {
              // Delete qualification
              const deletedQual = qualifications.find(
                q => q.id === aiResponse.id
              );
              const deleteResult = await complianceService.deleteRequirement(
                aiResponse.id
              );

              if (deleteResult.success) {
                setQualifications(prev =>
                  prev.filter(qual => qual.id !== aiResponse.id)
                );
                await addMessage({
                  role: 'assistant',
                  content: `✅ Removed: ${deletedQual?.name || 'qualification'}`
                });
              }
            } else if (aiResponse.action === 'bulk_add') {
              // Add multiple qualifications
              const saveResult =
                await complianceService.addMultipleRequirements(
                  projectId,
                  aiResponse.qualifications.map(qual => ({
                    name: qual.name,
                    description: qual.description,
                    category: qual.category,
                    priority: qual.priority,
                    renewal_period: qual.renewalPeriod,
                    status: 'pending',
                    regulatory_body: qual.regulatoryBody || '',
                    notes: qual.notes || '',
                    minimum_personnel: qual.minimumPersonnel || null,
                    calculated_required: qual.calculatedRequired || null
                  }))
                );

              if (saveResult.success) {
                setQualifications(prev => [...prev, ...saveResult.data]);
                await addMessage({
                  role: 'assistant',
                  content: `✅ Added ${saveResult.data.length} qualifications`
                });
              }
            } else if (aiResponse.action === 'clear_all') {
              // Clear all qualifications
              const deleteResult =
                await complianceService.deleteAllRequirements(projectId);

              if (deleteResult.success) {
                setQualifications([]);
                await addMessage({
                  role: 'assistant',
                  content: `✅ Cleared all qualifications`
                });
              }
            } else if (aiResponse.action === 'update_project') {
              // Update project section with new summary text
              const section = aiResponse.section; // 'about', 'workforce', or 'risk'
              const value = aiResponse.value;

              // Construct updated collected data
              const updatedCollectedData = {
                ...wizardState.collectedData,
                [section]: value
              };

              // Update wizard state with the new section summary
              setWizardState(prev => ({
                ...prev,
                collectedData: updatedCollectedData,
                sectionSummaries: {
                  ...prev.sectionSummaries,
                  [section]: value
                }
              }));

              // Save to database
              await complianceService.saveWizardData(projectId, {
                collectedData: updatedCollectedData,
                sectionSummaries: {
                  ...wizardState.sectionSummaries,
                  [section]: value
                }
              });

              // Update ChatFlows embed with configuration changes - always send all data
              if (chatFlowsRef.current) {
                chatFlowsRef.current.update(
                  getAllEmbedData(updatedCollectedData)
                );
              }

              await addMessage({
                role: 'assistant',
                content: `✅ Updated ${section} section:\n\n${value}`
              });

              // Always show regenerate button when project data changes
              setShowRegenerateButton(true);
            } else {
              // Default management message
              messageContent =
                aiResponse.message ||
                'How can I help you manage your requirements?';
            }
          }

          // Update wizard state if needed
          if (shouldUpdateState) {
            setWizardState(prev => ({
              ...prev,
              ...stateUpdate
            }));
          }

          // Clear old suggested responses unless we're setting new ones
          if (!aiResponse.suggestions && !stateUpdate.currentSection) {
            setSuggestedResponses([]);
          }

          // Always add message if we have content - addMessage will validate
          if (messageContent) {
            await addMessage({
              role: 'assistant',
              content: messageContent
            });
          } else if (!shouldUpdateState) {
            // If no message and no state update, provide fallback
            await addMessage({
              role: 'assistant',
              content: wizardMode
                ? 'Please tell me more about your requirements.'
                : 'How can I help you manage your compliance requirements?'
            });
          }
        } else {
          console.error('AI request failed:', result.error);
          await addMessage({
            role: 'assistant',
            content: `❌ I encountered an error: ${
              result.error || 'Unknown error'
            }. Please try again.`
          });
        }
      } catch (error) {
        console.error('Chat error:', error);
        await addMessage({
          role: 'assistant',
          content: '❌ I encountered an error. Please try again.'
        });
      } finally {
        setIsChatting(false);
        // Refocus the input
        setTimeout(() => chatInputRef.current?.focus(), 100);
      }
    }
  };

  const handleDeleteRequirement = async requirementId => {
    const result = await complianceService.deleteRequirement(requirementId);
    if (result.success) {
      setQualifications(prev => prev.filter(q => q.id !== requirementId));
    }
  };

  const handleClearAllQualifications = () => {
    setShowClearConfirmDialog(true);
  };

  const confirmClearAllQualifications = async () => {
    setShowClearConfirmDialog(false);

    try {
      console.log('🗑️ Clearing all qualifications for project:', projectId);
      const result = await complianceService.deleteAllRequirements(projectId);

      if (result.success) {
        setQualifications([]);
        console.log('✅ All qualifications cleared');
        toast.success('All requirements cleared', {
          description: `Removed ${qualifications.length} requirements`
        });
      } else {
        console.error('❌ Failed to clear qualifications:', result.error);
        toast.error('Failed to clear requirements', {
          description: result.error
        });
      }
    } catch (error) {
      console.error('❌ Error clearing qualifications:', error);
      toast.error('Error clearing requirements', {
        description: error.message
      });
    }
  };

  const handleEditRequirement = requirement => {
    setEditingRequirement(requirement);
    setEditFormData({
      name: requirement.name || '',
      description: requirement.description || '',
      category: requirement.category || 'Training',
      priority: requirement.priority || 'medium',
      renewal_period: requirement.renewal_period || '',
      regulatory_body: requirement.regulatory_body || '',
      regulation_reference: requirement.regulation_reference || '',
      legal_requirement: requirement.legal_requirement || false,
      notes: requirement.notes || '',
      status: requirement.status || 'pending'
    });
  };

  const handleSaveEdit = async () => {
    if (!editingRequirement) return;

    setIsSaving(true);
    const result = await complianceService.updateRequirement(
      editingRequirement.id,
      editFormData
    );

    if (result.success) {
      setQualifications(prev =>
        prev.map(q =>
          q.id === editingRequirement.id ? { ...q, ...editFormData } : q
        )
      );
      setEditingRequirement(null);
      setEditFormData({});
    }
    setIsSaving(false);
  };

  // Handle approving a requirement
  const handleApproveRequirement = async (requirementId) => {
    const approverName = userProfile?.full_name ||
      `${userProfile?.first_name || ''} ${userProfile?.last_name || ''}`.trim() ||
      userProfile?.email;
    const approvalData = {
      approved_by: userProfile?.id,
      approved_by_name: approverName,
      approved_at: new Date().toISOString()
    };

    const result = await complianceService.updateRequirement(requirementId, {
      approval_status: 'approved',
      approval_data: approvalData
    });

    if (result.success) {
      setQualifications(prev =>
        prev.map(q =>
          q.id === requirementId
            ? { ...q, approval_status: 'approved', approval_data: approvalData }
            : q
        )
      );
      toast.success('Requirement approved');
    } else {
      toast.error('Failed to approve requirement');
    }
  };

  // Handle rejecting a requirement
  const handleRejectRequirement = async (requirementId) => {
    const rejecterName = userProfile?.full_name ||
      `${userProfile?.first_name || ''} ${userProfile?.last_name || ''}`.trim() ||
      userProfile?.email;
    const approvalData = {
      rejected_by: userProfile?.id,
      rejected_by_name: rejecterName,
      rejected_at: new Date().toISOString()
    };

    const result = await complianceService.updateRequirement(requirementId, {
      approval_status: 'rejected',
      approval_data: approvalData
    });

    if (result.success) {
      setQualifications(prev =>
        prev.map(q =>
          q.id === requirementId
            ? { ...q, approval_status: 'rejected', approval_data: approvalData }
            : q
        )
      );
      toast.success('Requirement rejected');
    } else {
      toast.error('Failed to reject requirement');
    }
  };

  const handleGenerateAssignments = async (requirement) => {
    try {
      // If this is a different requirement, clear old recommendations
      if (selectedRequirementForAssignment?.id !== requirement.id) {
        setAssignmentRecommendations(null);
        setCurrentAssignments([]);
        setCompanyWideTrainedMembers([]);
        setCompanyWideAllMembers([]);
      }

      // Set the selected requirement and show dialog with loader
      setSelectedRequirementForAssignment(requirement);
      setShowAssignmentDialog(true);
      setIsGeneratingAssignments(true);

      // Fetch team members for this project
      console.log('🔍 Fetching space_members for projectId:', projectId);
      const { data: members, error: membersError } = await supabase
        .from('space_members')
        .select(`
          id,
          user_id,
          role,
          department,
          status,
          user_profiles!space_members_user_id_fkey (
            id,
            first_name,
            last_name,
            email,
            job_title,
            department,
            avatar_url
          )
        `)
        .eq('project_id', projectId)
        .eq('status', 'active');

      console.log('📊 Fetched members:', members?.map(m => ({ id: m.id, user_id: m.user_id, status: m.status })));

      if (membersError) {
        console.error('Error fetching team members:', membersError);
        throw membersError;
      }

      // Format members for assignment lookups
      const formattedMembers = (members || []).map(sm => ({
        id: sm.id, // space_members.id (used in compliance_assignments.team_member_id)
        space_member_id: sm.id,
        user_id: sm.user_id,
        name: `${sm.user_profiles.first_name || ''} ${sm.user_profiles.last_name || ''}`.trim() || sm.user_profiles.email,
        email: sm.user_profiles.email,
        role: sm.role,
        department: sm.department,
        status: sm.status,
        avatar: sm.user_profiles.avatar_url
      }));

      // Store these members for use with recommendations
      setRecommendationMembers(formattedMembers);
      console.log('✅ Stored recommendation members:', formattedMembers.map(m => ({ id: m.id, user_id: m.user_id, name: m.name })));

      // Fetch user IDs for all members
      const memberUserIds = members.map(m => m.user_id);

      // Fetch qualifications for this specific requirement for space members
      // Status 'completed' means they have the qualification
      const { data: spaceQualifications, error: spaceQualError } = await supabase
        .from('user_qualifications')
        .select(`
          id,
          user_id,
          status,
          expiry_date,
          user_profiles!user_qualifications_user_id_fkey (
            id,
            first_name,
            last_name,
            email,
            job_title,
            department,
            avatar_url
          )
        `)
        .eq('requirement_id', requirement.id)
        .in('user_id', memberUserIds)
        .eq('status', 'completed');

      if (spaceQualError) {
        console.error('Error fetching space qualifications:', spaceQualError);
        throw spaceQualError;
      }

      // Build alreadyTrained list from space qualifications
      const alreadyTrained = (spaceQualifications || []).map(q => {
        const member = formattedMembers.find(m => m.user_id === q.user_id);
        return {
          employeeId: member?.id || q.user_id,
          userId: q.user_id,
          name: `${q.user_profiles.first_name || ''} ${q.user_profiles.last_name || ''}`.trim(),
          email: q.user_profiles.email,
          jobTitle: q.user_profiles.job_title,
          department: q.user_profiles.department,
          avatar: q.user_profiles.avatar_url,
          status: q.status,
          expiryDate: q.expiry_date
        };
      });

      console.log('✅ Space trained members:', alreadyTrained);

      // Fetch company-wide qualifications for this requirement (all users in company, not just this space)
      // Use course_link_id/type to match across spaces (since requirement_id is space-specific)
      let companyQualifications = [];
      let companyQualError = null;

      if (requirement.course_link_id && requirement.course_link_type) {
        // Query by course_link_id/type to find everyone company-wide with this qualification
        const { data, error } = await supabase
          .from('user_qualifications')
          .select(`
            id,
            user_id,
            status,
            expiry_date,
            user_profiles!user_qualifications_user_id_fkey (
              id,
              first_name,
              last_name,
              email,
              job_title,
              department,
              avatar_url
            )
          `)
          .eq('course_link_id', requirement.course_link_id)
          .eq('course_link_type', requirement.course_link_type)
          .eq('status', 'completed');

        companyQualifications = data || [];
        companyQualError = error;
        console.log('🌐 Company-wide query by course_link:', {
          course_link_id: requirement.course_link_id,
          course_link_type: requirement.course_link_type,
          results: companyQualifications.length
        });
      } else {
        // Fallback to requirement_id if no course_link
        const { data, error } = await supabase
          .from('user_qualifications')
          .select(`
            id,
            user_id,
            status,
            expiry_date,
            user_profiles!user_qualifications_user_id_fkey (
              id,
              first_name,
              last_name,
              email,
              job_title,
              department,
              avatar_url
            )
          `)
          .eq('requirement_id', requirement.id)
          .eq('status', 'completed');

        companyQualifications = data || [];
        companyQualError = error;
      }

      if (companyQualError) {
        console.error('Error fetching company qualifications:', companyQualError);
        // Don't throw - company-wide is optional
      }

      // Build company-wide trained list (excluding space members who are already shown)
      const companyWideTrained = (companyQualifications || [])
        .filter(q => !memberUserIds.includes(q.user_id)) // Exclude people already in this space
        .map(q => ({
          employeeId: q.user_id, // Use user_id as employeeId for company-wide members
          userId: q.user_id,
          name: `${q.user_profiles?.first_name || ''} ${q.user_profiles?.last_name || ''}`.trim(),
          email: q.user_profiles?.email,
          jobTitle: q.user_profiles?.job_title,
          department: q.user_profiles?.department,
          avatar: q.user_profiles?.avatar_url,
          status: q.status,
          expiryDate: q.expiry_date
        }));

      console.log('✅ Company-wide trained members:', companyWideTrained);
      setCompanyWideTrainedMembers(companyWideTrained);

      // Fetch ALL company members not in this space (for "Other Company Members" section)
      const companyId = company?.id || space?.company_id;
      let companyMembersNotInSpace = []; // Declare at higher scope for use in allUserIds
      console.log('🏢 Company ID check:', { companyId, companyFromContext: company?.id, spaceCompanyId: space?.company_id });
      if (companyId) {
        console.log('🏢 Fetching company members for companyId:', companyId);
        try {
          const { data: allCompanyMembers, error: allCompanyError } = await supabase
            .from('company_members')
            .select(`
              id,
              user_id,
              role,
              user_profiles!company_members_user_id_fkey (
                id,
                first_name,
                last_name,
                email,
                job_title,
                department,
                avatar_url
              )
            `)
            .eq('company_id', companyId);

          console.log('🏢 Company members query completed:', {
            hasError: !!allCompanyError,
            dataLength: allCompanyMembers?.length,
            error: allCompanyError
          });

          if (allCompanyError) {
            console.error('Error fetching all company members:', allCompanyError);
          } else {
            console.log('📊 Raw company members from query:', allCompanyMembers?.length || 0);
            console.log('📊 Space member user IDs:', memberUserIds);

            // Filter out members already in this space
            companyMembersNotInSpace = (allCompanyMembers || [])
              .filter(cm => !memberUserIds.includes(cm.user_id))
              .map(cm => ({
                employeeId: cm.user_id,
                userId: cm.user_id,
                name: `${cm.user_profiles?.first_name || ''} ${cm.user_profiles?.last_name || ''}`.trim(),
                email: cm.user_profiles?.email,
                jobTitle: cm.user_profiles?.job_title,
                department: cm.user_profiles?.department,
                avatar: cm.user_profiles?.avatar_url
              }));

            console.log('✅ All company members not in space:', companyMembersNotInSpace.length, companyMembersNotInSpace);
            setCompanyWideAllMembers(companyMembersNotInSpace);
          }
        } catch (queryError) {
          console.error('🚨 Exception fetching company members:', queryError);
        }
      } else {
        console.warn('⚠️ No company ID available for fetching company members');
      }

      // Fetch current assignments for this requirement in this space
      console.log('📋 Fetching assignments for:', {
        projectId,
        requirementId: requirement.id,
        requirementName: requirement.name
      });

      const { data: existingAssignments, error: assignmentsError } = await supabase
        .from('compliance_assignments')
        .select('team_member_id, assigned')
        .eq('project_id', projectId)
        .eq('requirement_id', requirement.id)
        .eq('assigned', true);

      if (assignmentsError) {
        console.error('Error fetching assignments:', assignmentsError);
      }

      console.log('✅ Current assignments in THIS space:', existingAssignments);
      setCurrentAssignments(existingAssignments || []);

      // Fetch qualifications for all members (for displaying chips)
      // Space members: filter by this project's requirements
      // Company-wide members: fetch ALL qualifications across all spaces

      const qualsByUser = {};

      // 1. Fetch qualifications for SPACE members (filtered by this project)
      if (memberUserIds.length > 0) {
        const { data: spaceQuals, error: spaceQualsError } = await supabase
          .from('user_qualifications')
          .select(`
            id,
            user_id,
            requirement_id,
            status,
            expiry_date,
            compliance_requirements!inner (
              id,
              name,
              category
            )
          `)
          .in('user_id', memberUserIds)
          .eq('compliance_requirements.project_id', projectId);

        if (spaceQualsError) {
          console.error('Error fetching space member qualifications:', spaceQualsError);
        } else {
          (spaceQuals || []).forEach(q => {
            if (!qualsByUser[q.user_id]) {
              qualsByUser[q.user_id] = [];
            }
            qualsByUser[q.user_id].push({
              requirementId: q.requirement_id,
              requirementName: q.compliance_requirements?.name,
              category: q.compliance_requirements?.category,
              status: q.status,
              expiryDate: q.expiry_date
            });
          });
          console.log('✅ Fetched qualifications for', memberUserIds.length, 'space members');
        }
      }

      // 2. Fetch ALL qualifications for COMPANY-WIDE members (not filtered by project)
      const companyWideUserIds = [
        ...companyWideTrained.map(m => m.userId),
        ...(companyMembersNotInSpace || []).map(m => m.userId)
      ].filter((id, idx, arr) => arr.indexOf(id) === idx && !memberUserIds.includes(id));

      console.log('🏢 Company-wide user IDs to fetch qualifications for:', companyWideUserIds);

      if (companyWideUserIds.length > 0) {
        const { data: companyQuals, error: companyQualsError } = await supabase
          .rpc('get_all_user_qualifications', { p_user_ids: companyWideUserIds });

        console.log('🏢 RPC get_all_user_qualifications result:', {
          error: companyQualsError,
          dataLength: companyQuals?.length,
          sampleData: companyQuals?.slice(0, 3)
        });

        if (companyQualsError) {
          console.error('Error fetching company-wide qualifications:', companyQualsError);
        } else {
          (companyQuals || []).forEach(q => {
            if (!qualsByUser[q.user_id]) {
              qualsByUser[q.user_id] = [];
            }
            qualsByUser[q.user_id].push({
              requirementId: q.requirement_id,
              requirementName: q.requirement_name,
              category: q.category,
              status: q.status,
              expiryDate: q.expiry_date,
              projectId: q.project_id,
              projectName: q.project_name,
              complianceStatus: q.compliance_status
            });
          });
          console.log('✅ Fetched ALL qualifications for', companyWideUserIds.length, 'company-wide members, quals:', companyQuals?.length);
        }
      } else {
        console.log('🏢 No company-wide user IDs to fetch qualifications for');
      }

      console.log('✅ Total users with qualifications:', Object.keys(qualsByUser).length);
      console.log('🔍 qualsByUser keys:', Object.keys(qualsByUser));
      console.log('🔍 qualsByUser sample:', Object.entries(qualsByUser).slice(0, 2));
      setMemberQualifications(qualsByUser);

      // Build recommendations data locally (no N8N)
      const recommendations = {
        alreadyTrained: alreadyTrained,
        requirementId: requirement.id,
        requirementName: requirement.name
      };

      console.log('✅ Built recommendations locally:', recommendations);

      setAssignmentRecommendations(recommendations);
      setIsGeneratingAssignments(false);

    } catch (error) {
      console.error('❌ Error generating assignments:', error);
      setIsGeneratingAssignments(false);
      // Show error in dialog
      setAssignmentRecommendations({
        error: true,
        message: error.message || 'Failed to load assignment recommendations'
      });
    }
  };

  // NOTE: Client-side generation functions removed
  // All recommendation generation now handled by database triggers and RPC
  // See: trigger_recommendation_generation() and generate_pending_recommendations() in database

  // Regenerate recommendations for a single requirement
  const handleRegenerateRecommendations = async (requirement) => {
    try {
      console.log('🔄 Regenerating recommendations for:', requirement.name);

      // SAFETY NET: Remove from processed set to allow re-processing
      processedRequirementsRef.current.delete(requirement.id);
      console.log(`🛡️ Removed ${requirement.id} from processed set for regeneration`);

      // Update local state immediately to show generating
      setQualifications(prev => prev.map(q =>
        q.id === requirement.id
          ? {
              ...q,
              recommendations_status: 'generating',
              recommendations_data: null,
              recommendations_generated_date: null
            }
          : q
      ));

      toast.info(`Regenerating recommendations for ${requirement.name}...`);

      // Get user's session token for authentication
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session');
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

      console.log('📤 Calling Edge Function for:', requirement.name);

      // Call Edge Function for this specific requirement and wait for completion
      const response = await fetch(`${supabaseUrl}/functions/v1/generate-recommendations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ requirementId: requirement.id })
      });

      console.log('📥 Edge Function response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to regenerate: ${errorText}`);
      }

      const result = await response.json();
      console.log(`✅ Successfully regenerated for: ${requirement.name}`, result);

      // Reload just this requirement's data
      const { data: updated, error: fetchError } = await supabase
        .from('compliance_requirements')
        .select('*')
        .eq('id', requirement.id)
        .single();

      if (fetchError) {
        console.error('Error fetching updated requirement:', fetchError);
      } else {
        // Update local state with fresh data
        setQualifications(prev => prev.map(q =>
          q.id === requirement.id ? updated : q
        ));
      }

      toast.success(`Recommendations regenerated for ${requirement.name}!`);

    } catch (error) {
      console.error('❌ Error regenerating recommendations:', error);
      toast.error('Failed to regenerate recommendations', {
        description: error.message
      });

      // Update local state to show error
      setQualifications(prev => prev.map(q =>
        q.id === requirement.id
          ? { ...q, recommendations_status: 'error' }
          : q
      ));
    }
  };

  // Helper to check if recommendations data is valid (has actual recommendations, not just error messages)
  const hasValidRecommendations = (data) => {
    if (!data) return false;
    try {
      // Parse if string (may be double-encoded)
      let parsed = data;
      if (typeof parsed === 'string') {
        parsed = JSON.parse(parsed);
        // Check if still a string (double-encoded)
        if (typeof parsed === 'string') {
          parsed = JSON.parse(parsed);
        }
      }
      // Check if it's an array with recommendations
      if (Array.isArray(parsed) && parsed.length > 0) {
        // Check if first item has actual recommendations (not empty)
        const first = parsed[0];
        if (first && first.recommendations && Array.isArray(first.recommendations) && first.recommendations.length > 0) {
          return true;
        }
        // Also valid if there are alreadyTrained members
        if (first && first.alreadyTrained && Array.isArray(first.alreadyTrained) && first.alreadyTrained.length > 0) {
          return true;
        }
      }
      // Check for error messages indicating bad data
      if (parsed && typeof parsed === 'object' && parsed.message) {
        return false; // "Workflow was started" or similar
      }
      return false;
    } catch (e) {
      console.warn('hasValidRecommendations parse error:', e);
      return false;
    }
  };

  // Manually trigger recommendations generation via RPC
  const handleRefreshRecommendations = async () => {
    try {
      console.log('🔄 Generating recommendations via Edge Function for project:', projectId);

      setIsGeneratingRecs(true);

      // Always fetch fresh requirements from database to avoid stale state issues
      console.log('📥 Fetching fresh requirements from database...');
      const freshResult = await complianceService.loadProjectCompliance(projectId);
      const freshRequirements = freshResult.success ? freshResult.requirements : [];

      console.log('📊 Fresh requirements from DB:', freshRequirements.length);

      // Find all course-type requirements (not checklists or documentation)
      const courseRequirements = freshRequirements.filter(
        q => q.requirement_type !== 'internal_checklist' &&
             q.requirement_type !== 'documentation'
      );

      console.log('📊 Course requirements found:', courseRequirements.length);
      console.log('📊 All requirements:', freshRequirements.map(q => ({ name: q.name, type: q.requirement_type, status: q.recommendations_status })));

      if (courseRequirements.length === 0) {
        toast.info('No course requirements found', {
          description: 'Only course-type requirements can have recommendations generated'
        });
        setIsGeneratingRecs(false);
        return;
      }

      // For now, regenerate ALL course requirements to fix bad data
      const requirementsToProcess = courseRequirements;

      toast.info(`Generating recommendations for ${requirementsToProcess.length} requirement(s)...`, {
        description: 'Please wait, this may take a few moments'
      });

      // Get user's session token for authentication
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session');
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

      // Call Edge Function for each requirement and wait for completion
      // Use longer timeout (2 minutes) since N8N can take a while
      const results = await Promise.allSettled(
        requirementsToProcess.map(async (req) => {
          console.log(`📤 Calling Edge Function for: ${req.name} (id: ${req.id})`);

          // Mark as processed immediately
          processedRequirementsRef.current.add(req.id);

          // Create abort controller with 2 minute timeout
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 120000);

          try {
            const response = await fetch(`${supabaseUrl}/functions/v1/generate-recommendations`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`
              },
              body: JSON.stringify({ requirementId: req.id }),
              signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
              const errorText = await response.text();
              throw new Error(`Failed for ${req.name}: ${errorText}`);
            }

            const result = await response.json();
            console.log(`✅ Edge Function completed for: ${req.name}`);
            return result;
          } catch (error) {
            clearTimeout(timeoutId);
            if (error.name === 'AbortError') {
              throw new Error(`Timeout for ${req.name} - took longer than 2 minutes`);
            }
            throw error;
          }
        })
      );

      // Count successes and failures
      const successful = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      if (failed > 0) {
        toast.warning(`Generated ${successful} of ${requirementsToProcess.length} recommendations`, {
          description: `${failed} failed - check console for details`
        });

        // Mark failed requirements with error status in database
        for (let i = 0; i < results.length; i++) {
          if (results[i].status === 'rejected') {
            const failedReq = requirementsToProcess[i];
            console.error(`Failed for ${failedReq.name}:`, results[i].reason);

            // Update the requirement's status to 'error' so it doesn't show as pending forever
            try {
              await supabase
                .from('compliance_requirements')
                .update({ recommendations_status: 'error' })
                .eq('id', failedReq.id);
              console.log(`📝 Marked ${failedReq.name} as error status`);
            } catch (updateError) {
              console.error(`Failed to update error status for ${failedReq.name}:`, updateError);
            }
          }
        }
      } else {
        toast.success(`Successfully generated all ${successful} recommendations!`);
      }

      // Small delay to ensure database has committed all changes
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Refresh data to show completed recommendations
      console.log('🔄 Refreshing compliance data after recommendations generated...');
      const refreshResult = await complianceService.loadProjectCompliance(projectId);
      if (refreshResult.success && refreshResult.requirements) {
        console.log('✅ Refreshed requirements:', refreshResult.requirements.length);
        console.log('📊 Recommendations statuses:', refreshResult.requirements.map(r => ({
          name: r.name,
          status: r.recommendations_status
        })));
        // Force state update and re-render
        setQualifications([...refreshResult.requirements]);
        setQualificationsKey(prev => prev + 1); // Force re-render
      } else {
        console.error('❌ Failed to refresh requirements:', refreshResult.error);
      }

    } catch (error) {
      console.error('❌ Error generating recommendations:', error);
      toast.error('Failed to generate recommendations', {
        description: error.message || 'Unknown error occurred'
      });
    } finally {
      setIsGeneratingRecs(false);
    }
  };

  // Retry recommendation generation for a single failed requirement
  const handleRetryRecommendation = async (requirementId, requirementName) => {
    try {
      console.log(`🔄 Retrying recommendation generation for: ${requirementName}`);
      toast.info(`Retrying recommendations for ${requirementName}...`);

      // Mark as pending again
      await supabase
        .from('compliance_requirements')
        .update({ recommendations_status: 'pending' })
        .eq('id', requirementId);

      // Update local state to show pending
      setQualifications(prev => prev.map(q =>
        q.id === requirementId ? { ...q, recommendations_status: 'pending' } : q
      ));

      // Get user's session token for authentication
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session');
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

      // Create abort controller with 2 minute timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000);

      try {
        const response = await fetch(`${supabaseUrl}/functions/v1/generate-recommendations`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({ requirementId }),
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText);
        }

        await response.json();
        console.log(`✅ Retry successful for: ${requirementName}`);
        toast.success(`Recommendations generated for ${requirementName}`);

        // Refresh to get updated data
        const refreshResult = await complianceService.loadProjectCompliance(projectId);
        if (refreshResult.success && refreshResult.requirements) {
          setQualifications([...refreshResult.requirements]);
          setQualificationsKey(prev => prev + 1);
        }
      } catch (fetchError) {
        clearTimeout(timeoutId);
        const errorMessage = fetchError.name === 'AbortError'
          ? 'Request timed out (2 minutes)'
          : fetchError.message;

        // Mark as error again
        await supabase
          .from('compliance_requirements')
          .update({ recommendations_status: 'error' })
          .eq('id', requirementId);

        setQualifications(prev => prev.map(q =>
          q.id === requirementId ? { ...q, recommendations_status: 'error' } : q
        ));

        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error(`❌ Retry failed for ${requirementName}:`, error);
      toast.error(`Failed to retry ${requirementName}`, {
        description: error.message
      });
    }
  };

  // NOTE: Auto-generation is now handled by database triggers
  // When a compliance requirement is inserted, a trigger automatically calls N8N
  // Use the "Generate Recommendations" button to manually trigger for pending requirements

  // Dynamic generation progress over 90 seconds
  useEffect(() => {
    if (!isGenerating) {
      // Reset to initial state when not generating
      setGenerationProgress({
        stage: 0,
        message: 'Initializing AI analysis...',
        substeps: []
      });
      return;
    }

    // Define progress stages with timing (in seconds)
    const stages = [
      {
        time: 0,
        stage: 0,
        message: 'Initializing AI analysis...',
        substeps: ['Connecting to compliance engine', 'Loading industry data']
      },
      {
        time: 8,
        stage: 1,
        message: 'Researching industry regulations...',
        substeps: ['Analyzing sector-specific requirements', 'Reviewing legal frameworks', 'Identifying mandatory standards']
      },
      {
        time: 20,
        stage: 2,
        message: 'Analyzing your business context...',
        substeps: ['Processing team structure', 'Evaluating work environment', 'Assessing operational risks']
      },
      {
        time: 35,
        stage: 3,
        message: 'Identifying compliance requirements...',
        substeps: ['Matching regulations to your operations', 'Prioritizing critical requirements', 'Building requirement library']
      },
      {
        time: 52,
        stage: 4,
        message: 'Generating training courses...',
        substeps: ['Creating mandatory training programs', 'Assigning certification requirements', 'Setting renewal periods']
      },
      {
        time: 68,
        stage: 5,
        message: 'Creating checklists and documentation...',
        substeps: ['Designing inspection checklists', 'Generating policy templates', 'Building audit frameworks']
      },
      {
        time: 82,
        stage: 6,
        message: 'Finalizing requirements...',
        substeps: ['Validating compliance coverage', 'Optimizing requirement structure', 'Preparing recommendations']
      }
    ];

    let currentStage = 0;
    const startTime = Date.now();

    const updateProgress = () => {
      const elapsed = (Date.now() - startTime) / 1000; // seconds

      // Find the current stage based on elapsed time
      for (let i = stages.length - 1; i >= 0; i--) {
        if (elapsed >= stages[i].time) {
          if (currentStage !== stages[i].stage) {
            currentStage = stages[i].stage;
            setGenerationProgress(stages[i]);
          }
          break;
        }
      }
    };

    // Update progress every 500ms
    const interval = setInterval(updateProgress, 500);

    // Initial update
    updateProgress();

    return () => clearInterval(interval);
  }, [isGenerating]);

  // Handle assigning a user from recommendations (same pattern as matrix screen)
  const handleAssignFromRecommendation = async (userId, userName) => {
    if (!selectedRequirementForAssignment || !projectId) return;

    try {
      const requirement = selectedRequirementForAssignment;

      console.log('🔍 Looking for team member in database:', {
        employeeId: userId,
        userName,
        projectId
      });

      // Try finding by space_member.id first
      let member = null;
      const { data: byId } = await supabase
        .from('space_members')
        .select('id, user_id, role, department, status')
        .eq('project_id', projectId)
        .eq('id', userId)
        .eq('status', 'active')
        .maybeSingle();

      if (byId) {
        member = byId;
      } else {
        // Try by user_id (for when employeeId is actually user_profiles.id)
        const { data: byUserId } = await supabase
          .from('space_members')
          .select('id, user_id, role, department, status')
          .eq('project_id', projectId)
          .eq('user_id', userId)
          .eq('status', 'active')
          .maybeSingle();

        member = byUserId;
      }

      if (!member) {
        console.error('❌ Team member not found in database:', {
          searchingForUserId: userId,
          projectId
        });
        toast.error('Team member not found in this space');
        return;
      }

      console.log('✅ Found member:', member);

      // Step 1: Check if user already has this qualification globally
      let existingQual = null;

      if (requirement.course_link_id && requirement.course_link_type) {
        const { data: qualByCourseLink } = await supabase
          .from('user_qualifications')
          .select('*')
          .eq('user_id', member.user_id)
          .eq('course_link_id', requirement.course_link_id)
          .eq('course_link_type', requirement.course_link_type)
          .maybeSingle();

        existingQual = qualByCourseLink;
      }

      if (!existingQual) {
        const { data: qualByRequirement } = await supabase
          .from('user_qualifications')
          .select('*')
          .eq('user_id', member.user_id)
          .eq('requirement_id', requirement.id)
          .maybeSingle();

        existingQual = qualByRequirement;
      }

      // Step 2: Create space-specific assignment in compliance_assignments (just tracking flag)
      const { data: assignmentData, error: assignmentError } = await supabase
        .from('compliance_assignments')
        .upsert({
          project_id: projectId,
          requirement_id: requirement.id,
          team_member_id: member.user_id, // References user_profiles.id
          assigned: true
        }, {
          onConflict: 'project_id,requirement_id,team_member_id'
        })
        .select()
        .single();

      if (assignmentError) throw assignmentError;

      // Step 3: Create/update user_qualification if none exists
      if (!existingQual) {
        const qualificationData = {
          user_id: member.user_id,
          requirement_id: requirement.id,
          course_link_id: requirement.course_link_id,
          course_link_type: requirement.course_link_type,
          status: null, // NULL = missing (no evidence yet)
          assigned_date: new Date().toISOString(),
          assigned_by: userProfile?.id,
          due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          notes: 'Requirement assigned from AI recommendations',
          updated_date: new Date().toISOString()
        };

        const { error: qualError } = await supabase
          .from('user_qualifications')
          .upsert(qualificationData, {
            onConflict: 'user_id,requirement_id'
          });

        // Ignore 409 conflicts - they just mean the record already exists
        if (qualError && qualError.code !== '23505') {
          console.error('Error creating user_qualification:', qualError);
          throw qualError;
        }
      }

      // Update current assignments state
      setCurrentAssignments(prev => [...prev, { team_member_id: member.user_id, assigned: true }]);

      // Show success message
      if (existingQual) {
        toast.success(`${requirement.name} assigned to ${userName} (synced from existing qualification)`);
      } else {
        toast.success(`${requirement.name} assigned to ${userName}`);
      }

      // No need to reload entire page - button state will update via currentAssignments state

    } catch (error) {
      console.error('Error assigning from recommendation:', error);
      toast.error('Failed to assign requirement');
    }
  };

  // Handle unassigning a user from recommendations
  const handleUnassignFromRecommendation = async (userId, userName) => {
    if (!selectedRequirementForAssignment || !projectId) return;

    try {
      const requirement = selectedRequirementForAssignment;

      // Try finding by space_member.id first
      let spaceMember = null;
      const { data: byId } = await supabase
        .from('space_members')
        .select('id, user_id')
        .eq('project_id', projectId)
        .eq('id', userId)
        .eq('status', 'active')
        .maybeSingle();

      if (byId) {
        spaceMember = byId;
      } else {
        // Try by user_id
        const { data: byUserId } = await supabase
          .from('space_members')
          .select('id, user_id')
          .eq('project_id', projectId)
          .eq('user_id', userId)
          .eq('status', 'active')
          .maybeSingle();

        spaceMember = byUserId;
      }

      if (!spaceMember) {
        toast.error('Team member not found');
        return;
      }

      // Update the compliance_assignment to unassigned
      const { error: updateError } = await supabase
        .from('compliance_assignments')
        .update({ assigned: false })
        .eq('project_id', projectId)
        .eq('requirement_id', requirement.id)
        .eq('team_member_id', spaceMember.user_id);

      if (updateError) throw updateError;

      // Update local state
      setCurrentAssignments(prev =>
        prev.filter(a => a.team_member_id !== spaceMember.user_id)
      );

      toast.success(`${userName} unassigned from ${requirement.name}`);

    } catch (error) {
      console.error('Error unassigning:', error);
      toast.error('Failed to unassign');
    }
  };

  // Handle assigning a company-wide member (add to space + assign)
  const handleAssignCompanyWide = async (userId, userName, personData) => {
    if (!selectedRequirementForAssignment || !projectId || !company?.id) return;

    try {
      const requirement = selectedRequirementForAssignment;

      console.log('🌐 Adding company-wide member to space:', {
        userId,
        userName,
        projectId,
        companyId: company.id
      });

      // First, add the user to space_members
      const { data: newMember, error: addMemberError } = await supabase
        .from('space_members')
        .insert({
          project_id: projectId,
          user_id: userId,
          role: 'Member',
          status: 'active',
          created_date: new Date().toISOString()
        })
        .select('id, user_id, role, status')
        .single();

      if (addMemberError) {
        console.error('Error adding member to space:', addMemberError);
        toast.error(`Failed to add ${userName} to space`);
        return;
      }

      console.log('✅ Added member to space:', newMember);

      // Update the local recommendation members list to include the new member
      setRecommendationMembers(prev => [
        ...prev,
        {
          id: newMember.id,
          space_member_id: newMember.id,
          user_id: userId,
          name: userName,
          email: personData?.email,
          role: newMember.role,
          department: personData?.department,
          status: newMember.status,
          avatar: personData?.avatar
        }
      ]);

      // Now assign the requirement using the new space member
      // Check or create compliance_assignment
      const { data: existingAssignment } = await supabase
        .from('compliance_assignments')
        .select('id, assigned')
        .eq('project_id', projectId)
        .eq('requirement_id', requirement.id)
        .eq('team_member_id', userId)
        .maybeSingle();

      if (existingAssignment) {
        // Update existing assignment
        const { error: updateError } = await supabase
          .from('compliance_assignments')
          .update({ assigned: true })
          .eq('id', existingAssignment.id);

        if (updateError) throw updateError;
      } else {
        // Create new assignment
        const { error: insertError } = await supabase
          .from('compliance_assignments')
          .insert({
            project_id: projectId,
            requirement_id: requirement.id,
            team_member_id: userId,
            assigned: true
          });

        if (insertError) throw insertError;
      }

      // Update local state
      setCurrentAssignments(prev => [
        ...prev,
        { project_id: projectId, requirement_id: requirement.id, team_member_id: userId, assigned: true }
      ]);

      // Remove the member from company-wide lists (they're now in the space)
      setCompanyWideTrainedMembers(prev => prev.filter(m => m.employeeId !== userId));
      setCompanyWideAllMembers(prev => prev.filter(m => m.employeeId !== userId));

      toast.success(`${userName} added to space and assigned to ${requirement.name}`);

    } catch (error) {
      console.error('Error assigning company-wide member:', error);
      toast.error('Failed to assign');
    }
  };

  // Handle assigning all team members at once
  const handleAssignAll = async () => {
    if (!selectedRequirementForAssignment || !projectId || !recommendationMembers) return;

    try {
      const requirement = selectedRequirementForAssignment;

      // Filter out members who are already assigned
      const unassignedMembers = recommendationMembers.filter(
        member => !currentAssignments.some(a => a.team_member_id === member.user_id)
      );

      if (unassignedMembers.length === 0) {
        toast.info('All team members are already assigned');
        return;
      }

      let successCount = 0;
      let failCount = 0;

      // Assign each unassigned member
      for (const member of unassignedMembers) {
        try {
          // N8N returns space_member.id as employeeId, so query by id (not user_id)
          const { data: spaceMember, error: memberError } = await supabase
            .from('space_members')
            .select('id, user_id, role, department, status')
            .eq('project_id', projectId)
            .eq('id', member.id) // Query by space_member.id
            .eq('status', 'active')
            .single();

          if (memberError || !spaceMember) {
            console.error('Team member not found:', member.name);
            failCount++;
            continue;
          }

          // Check if user already has this qualification globally
          let existingQual = null;

          if (requirement.course_link_id && requirement.course_link_type) {
            const { data: qualByCourseLink } = await supabase
              .from('user_qualifications')
              .select('*')
              .eq('user_id', spaceMember.user_id)
              .eq('course_link_id', requirement.course_link_id)
              .eq('course_link_type', requirement.course_link_type)
              .maybeSingle();

            existingQual = qualByCourseLink;
          }

          if (!existingQual) {
            const { data: qualByRequirement } = await supabase
              .from('user_qualifications')
              .select('*')
              .eq('user_id', spaceMember.user_id)
              .eq('requirement_id', requirement.id)
              .maybeSingle();

            existingQual = qualByRequirement;
          }

          // Create space-specific assignment in compliance_assignments
          const { error: assignmentError } = await supabase
            .from('compliance_assignments')
            .upsert({
              project_id: projectId,
              requirement_id: requirement.id,
              team_member_id: spaceMember.user_id,
              assigned: true
            }, {
              onConflict: 'project_id,requirement_id,team_member_id'
            })
            .select()
            .single();

          if (assignmentError) {
            console.error('Assignment error for', member.name, assignmentError);
            failCount++;
            continue;
          }

          // Create/update user_qualification if none exists
          if (!existingQual) {
            const qualificationData = {
              user_id: spaceMember.user_id,
              requirement_id: requirement.id,
              course_link_id: requirement.course_link_id,
              course_link_type: requirement.course_link_type,
              status: null,
              assigned_date: new Date().toISOString(),
              assigned_by: userProfile?.id,
              due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
              notes: 'Requirement assigned from AI recommendations (bulk assign)',
              updated_date: new Date().toISOString()
            };

            const { error: qualError } = await supabase
              .from('user_qualifications')
              .upsert(qualificationData, {
                onConflict: 'user_id,requirement_id'
              });

            if (qualError && qualError.code !== '23505') {
              console.error('Qualification error for', member.name, qualError);
              failCount++;
              continue;
            }
          }

          // Update current assignments state
          setCurrentAssignments(prev => [...prev, { team_member_id: spaceMember.user_id, assigned: true }]);
          successCount++;

        } catch (error) {
          console.error('Error assigning to', member.name, error);
          failCount++;
        }
      }

      // Show summary toast
      if (successCount > 0 && failCount === 0) {
        toast.success(`${requirement.name} assigned to all ${successCount} team members`);
      } else if (successCount > 0 && failCount > 0) {
        toast.warning(`Assigned to ${successCount} members, ${failCount} failed`);
      } else {
        toast.error('Failed to assign to team members');
      }

    } catch (error) {
      console.error('Error in bulk assign:', error);
      toast.error('Failed to assign all team members');
    }
  };

  // Memoized helper function for priority colors
  const getPriorityColor = useCallback(priority => {
    switch (priority) {
      case 'high':
        return 'destructive';
      case 'medium':
        return 'secondary';
      case 'low':
        return 'outline';
      default:
        return 'outline';
    }
  }, []); // No dependencies, this function is pure

  const getStatusIcon = status => {
    switch (status) {
      case 'active':
        return <CheckCircle2 className='h-4 w-4 text-green-600' />;
      case 'expired':
        return <XCircle className='h-4 w-4 text-red-600' />;
      case 'pending':
        return <Clock className='h-4 w-4 text-yellow-600' />;
      default:
        return <Info className='h-4 w-4 text-gray-600' />;
    }
  };

  const getCategoryIcon = (category, requirementType) => {
    // Check requirement_type first for specific icons
    if (requirementType === 'internal_checklist') {
      return <ListChecks className='h-4 w-4' />;
    }
    if (requirementType === 'course') {
      return <Award className='h-4 w-4' />;
    }

    // Otherwise use category-based icons
    switch (category) {
      case 'Equipment':
        return <Briefcase className='h-4 w-4' />;
      case 'Safety':
        return <Shield className='h-4 w-4' />;
      case 'Compliance':
        return <FileCheck className='h-4 w-4' />;
      case 'Training':
        return <Users className='h-4 w-4' />;
      case 'Environmental':
        return <MapPin className='h-4 w-4' />;
      default:
        return <FileText className='h-4 w-4' />;
    }
  };

  // Helper function to calculate required count based on minimum_personnel and team size
  const getRequiredCount = useCallback(
    (requirement) => {
      const minimumPersonnel = requirement.minimum_personnel || '';
      const calculatedRequired = requirement.calculated_required;
      const totalTeamSize = teamMembers.length;

      // If minimum_personnel contains a percentage, calculate based on team size
      if (typeof minimumPersonnel === 'string' && minimumPersonnel.includes('%')) {
        const percentMatch = minimumPersonnel.match(/(\d+)%/);
        if (percentMatch) {
          const percentage = parseInt(percentMatch[1], 10);
          return Math.ceil(totalTeamSize * (percentage / 100));
        }
      }

      // If minimum_personnel indicates all staff
      const personnelLower = minimumPersonnel.toLowerCase();
      if (
        personnelLower.includes('all staff') ||
        personnelLower.includes('entire workforce') ||
        personnelLower.includes('all employees')
      ) {
        return totalTeamSize;
      }

      // Otherwise use calculated_required, capped at team size
      if (calculatedRequired) {
        return Math.min(calculatedRequired, totalTeamSize);
      }

      // Default to team size if no specific requirement
      return totalTeamSize;
    },
    [teamMembers.length]
  );

  // Smart matching function to match requirements to team members - memoized
  const getMatchingTeamMembers = useCallback(
    requirement => {
      const personnel = (requirement.minimum_personnel || '').toLowerCase();

      // Check for "all staff" patterns
      if (
        personnel.includes('all staff') ||
        personnel.includes('100%') ||
        personnel.includes('entire workforce') ||
        personnel.includes('all employees')
      ) {
        return teamMembers;
      }

      // Check for factory workers
      if (personnel.includes('factory worker')) {
        return teamMembers.filter(
          m =>
            m.department?.toLowerCase().includes('factory') ||
            m.role?.toLowerCase().includes('factory')
        );
      }

      // Check for managers/supervisors
      if (personnel.includes('manager') || personnel.includes('supervisor')) {
        return teamMembers.filter(
          m =>
            m.role?.toLowerCase().includes('manager') ||
            m.role?.toLowerCase().includes('supervisor')
        );
      }

      // Check for forklift operators
      if (personnel.includes('forklift')) {
        return teamMembers.filter(
          m =>
            m.department?.toLowerCase().includes('forklift') ||
            m.role?.toLowerCase().includes('forklift') ||
            m.department?.toLowerCase().includes('warehouse') ||
            m.department?.toLowerCase().includes('logistics')
        );
      }

      // Check for maintenance workers
      if (personnel.includes('maintenance')) {
        return teamMembers.filter(
          m =>
            m.department?.toLowerCase().includes('maintenance') ||
            m.role?.toLowerCase().includes('maintenance')
        );
      }

      // Check for janitors/cleaners
      if (personnel.includes('janitor') || personnel.includes('cleaner')) {
        return teamMembers.filter(
          m =>
            m.department?.toLowerCase().includes('janitor') ||
            m.role?.toLowerCase().includes('janitor') ||
            m.department?.toLowerCase().includes('cleaning')
        );
      }

      // Check for office/admin staff
      if (personnel.includes('office') || personnel.includes('admin')) {
        return teamMembers.filter(
          m =>
            m.department?.toLowerCase().includes('office') ||
            m.role?.toLowerCase().includes('admin') ||
            m.department?.toLowerCase().includes('marketing')
        );
      }

      // Check for ratio-based requirements (e.g., "1 per 20 employees")
      if (personnel.includes('per') && personnel.includes('employee')) {
        // For ratio-based, suggest managers or senior staff
        const managers = teamMembers.filter(
          m =>
            m.role?.toLowerCase().includes('manager') ||
            m.role?.toLowerCase().includes('supervisor')
        );

        // If we have managers, return them, otherwise return a subset of all staff
        if (managers.length > 0) {
          return managers;
        }

        // Calculate how many we need based on the ratio
        const match = personnel.match(/(\d+)\s*per\s*(\d+)/i);
        if (match) {
          const needed =
            Math.ceil(teamMembers.length / parseInt(match[2])) *
            parseInt(match[1]);
          return teamMembers.slice(0, needed);
        }
      }

      // Default: if we can't match specifically, return empty array
      return [];
    },
    [teamMembers]
  ); // Only recreate if teamMembers changes

  // Prepare assignment preview when dialog opens - optimized dependencies
  useEffect(() => {
    if (showAssignmentDialog && qualifications.length > 0) {
      const preview = qualifications.map(req => {
        const matchedMembers = getMatchingTeamMembers(req);
        return {
          requirement: req,
          suggestedMembers: matchedMembers,
          selectedMemberIds: matchedMembers.map(m => m.id) // Pre-select all matched members
        };
      });
      setAssignmentPreview(preview);
    }
  }, [showAssignmentDialog, qualifications, getMatchingTeamMembers]); // getMatchingTeamMembers is now stable

  const wizardProgress =
    (((companyName ? 1 : 0) +
      (industry ? 1 : 0) +
      (location ? 1 : 0) +
      (teamSize ? 1 : 0) +
      (projectDescription ? 1 : 0) +
      (specialRequirements ? 1 : 0)) /
      6) *
    100;

  if (isLoading) {
    return (
      <AppShell3>
        <div className='flex items-center justify-center h-64'>
          <Loader2 className='h-8 w-8 animate-spin text-primary' />
        </div>
      </AppShell3>
    );
  }

  return (
    <PageTransition>
      <AppShell3>
        <>
          {/* ToolHeader with custom actions */}
          <ToolHeader
            spaceName={space?.name || 'Space'}
            toolName='Team Training'
            currentTool='compliance'
            spaceTools={spaceTools}
            actions={
              <>
                {window.location.hostname === 'localhost' && qualifications.length > 0 && (
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={handleClearAllQualifications}
                    className='text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200'
                  >
                    <Trash2 className='h-4 w-4 mr-2' />
                    Clear ({qualifications.length})
                  </Button>
                )}
                {window.location.hostname === 'localhost' && (
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={handleRefreshRecommendations}
                    disabled={isGeneratingRecs}
                  >
                    {isGeneratingRecs ? (
                      <>
                        <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                        Generating...
                      </>
                    ) : (
                      <>
                        <RefreshCw className='h-4 w-4 mr-2' />
                        Generate Recommendations
                      </>
                    )}
                  </Button>
                )}

                {/* DEBUG BUTTON - Commented out for production */}
                {/* <Button
                  variant='destructive'
                  size='sm'
                  onClick={async () => {
                    if (
                      confirm(
                        '⚠️ DEBUG: This will reset ALL compliance data including requirements, wizard progress, and chat history. Are you sure?'
                      )
                    ) {
                      await resetComplianceData();
                    }
                  }}
                >
                  <RefreshCw className='h-4 w-4 mr-2' />
                  Reset (Debug)
                </Button> */}
              </>
            }
          />

          {/* Main Content - GPT Builder Style Layout with Sliding Panels */}
          <div className='w-full h-[calc(100vh-8rem)] bg-white overflow-hidden'>
            {/* Sliding container - holds all 3 panels, slides left when recommendations are open */}
            <div
              className={cn(
                'flex h-full border-t border-gray-200 transition-transform duration-500 ease-in-out',
                showAssignmentDialog ? '-translate-x-1/3' : 'translate-x-0'
              )}
              style={{ width: '150%' }} // 3 panels at 50% each = 150% total
            >
              {/* Panel 1 - ChatFlows (Left) */}
              <div className='w-1/3 bg-white flex flex-col overflow-hidden border-r border-gray-200 relative'>
                {/* ChatFlows Embed - Full height */}
                <ChatFlowsEmbed
                  key={projectId}
                  ref={chatFlowsRef}
                  publicKey='18453734a063321f'
                  apiUrl='https://chatflows.add.ai'
                  inline={true}
                  className='flex-1'
                  style={{ height: '100%' }}
                  showClose={false}
                  showExpand={false}
                  showTranscript={false}
                  disableRounding={true}
                  deferInit={true}
                  onReady={handleChatFlowsReady}
                />
              </div>

              {/* Panel 2 - Requirements Preview (Middle) */}
              <div className='w-1/3 bg-gray-50 flex flex-col overflow-hidden relative border-r border-gray-200'>
                {/* Preview Content */}
                <div className='flex-1 overflow-y-auto px-6 pt-6 pb-16 bg-gray-50'>
                  {isGenerating ? (
                    /* Loading state while generating requirements */
                    <div className='flex flex-col items-center justify-center h-full'>
                      <div className='text-center max-w-md'>
                        {/* Animated spinner with gradient */}
                        <div className='relative mb-6'>
                          <div className='absolute inset-0 flex items-center justify-center'>
                            <div className='h-16 w-16 rounded-full bg-gradient-to-tr from-purple-600 via-blue-500 to-green-500 animate-pulse opacity-20'></div>
                          </div>
                          <Loader2 className='h-12 w-12 animate-spin text-purple-600 mx-auto relative' />
                        </div>

                        <p className='text-gray-900 text-base font-semibold mb-2'>
                          {generationProgress.message}
                        </p>
                        <p className='text-gray-600 text-sm mb-4'>
                          Our AI is reviewing your business information and generating tailored compliance requirements...
                        </p>

                        {/* Dynamic progress steps */}
                        <div className='mt-6 space-y-2.5 text-left'>
                          {generationProgress.substeps.map((step, index) => (
                            <div key={index} className='flex items-center gap-3 text-xs text-gray-700'>
                              <div className='h-1.5 w-1.5 rounded-full bg-purple-600 animate-pulse'
                                   style={{ animationDelay: `${index * 0.2}s` }}></div>
                              <span>{step}</span>
                            </div>
                          ))}
                        </div>

                        {/* Progress bar */}
                        <div className='mt-8 w-full bg-gray-200 rounded-full h-1.5 overflow-hidden'>
                          <div
                            className='h-full bg-gradient-to-r from-purple-600 to-blue-500 transition-all duration-1000 ease-out'
                            style={{ width: `${(generationProgress.stage / 6) * 100}%` }}
                          ></div>
                        </div>

                        <p className='text-gray-400 text-xs mt-4'>
                          Stage {generationProgress.stage + 1} of 7 • Typically takes 60-90 seconds
                        </p>
                      </div>
                    </div>
                  ) : qualifications.length > 0 ? (
                    /* Show qualifications list */
                    <div className='pb-6'>
                      {(() => {
                        // Filter to only show qualifications (course type)
                        const filteredQualifications = qualifications.filter(q =>
                          (q.requirement_type || 'course') === 'course'
                        );

                        // Helper to get type-specific styling
                        const getTypeIcon = (type) => {
                          return <Award className='h-4 w-4 text-amber-600' />;
                        };

                        const getTypeLabel = (type) => {
                          return 'Qualifications';
                        };

                        if (filteredQualifications.length === 0) {
                          return (
                            <div className='text-center py-8 text-gray-500'>
                              <p className='text-sm'>No qualifications found</p>
                            </div>
                          );
                        }

                        return (
                          <Accordion
                            key={qualificationsKey}
                            type="multiple"
                            defaultValue={['course']}
                            className='space-y-3'
                          >
                            {[['course', filteredQualifications]].map(([type, quals]) => (
                              <AccordionItem
                                key={type}
                                value={type}
                                className='border rounded-lg bg-white shadow-sm'
                              >
                                <AccordionTrigger className='px-4 py-3 hover:no-underline hover:bg-gray-50 transition-colors'>
                                  <div className='flex items-center gap-3 w-full'>
                                    {getTypeIcon(type)}
                                    <span className='font-semibold text-sm text-gray-900'>
                                      {getTypeLabel(type)}
                                    </span>
                                    <Badge variant='secondary' className='text-xs ml-auto mr-2'>
                                      {quals.length} {quals.length === 1 ? 'item' : 'items'}
                                    </Badge>
                                  </div>
                                </AccordionTrigger>
                                <AccordionContent className='px-0 pb-0'>
                                  <div className='divide-y divide-gray-100'>
                                    {quals.map((qual, index) => (
                                      <div
                                        key={qual.id}
                                        className={cn(
                                          'px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer group',
                                          selectedRequirement?.id === qual.id && 'bg-blue-50 hover:bg-blue-50'
                                        )}
                                        onClick={() => setSelectedRequirement(qual)}
                                      >
                                        {/* Header row */}
                                        <div className='flex items-start justify-between gap-3 mb-2'>
                                          <div className='flex items-start gap-2 flex-1 min-w-0'>
                                            <div className='mt-0.5'>
                                              {getCategoryIcon(qual.category, qual.requirement_type)}
                                            </div>
                                            <div className='flex-1 min-w-0'>
                                              <h4 className='text-sm font-medium text-gray-900 mb-0.5'>
                                                {qual.name}
                                              </h4>
                                              {qual.description && (
                                                <p className='text-xs text-gray-600 line-clamp-2'>
                                                  {qual.description}
                                                </p>
                                              )}
                                            </div>
                                          </div>

                                          {/* Right side - edit/delete buttons */}
                                          <div className='flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity'>
                                            <Button
                                              variant='ghost'
                                              size='icon'
                                              className='h-7 w-7'
                                              onClick={e => {
                                                e.stopPropagation();
                                                handleEditRequirement(qual);
                                              }}
                                            >
                                              <Edit className='h-3 w-3' />
                                            </Button>
                                            <Button
                                              variant='ghost'
                                              size='icon'
                                              className='h-7 w-7 text-red-600 hover:text-red-700 hover:bg-red-50'
                                              onClick={e => {
                                                e.stopPropagation();
                                                setDeleteRequirementId(qual.id);
                                              }}
                                            >
                                              <Trash2 className='h-3 w-3' />
                                            </Button>
                                          </div>
                                        </div>

                                        {/* Badges row - always visible */}
                                        <div className='flex flex-wrap items-center gap-1.5 ml-6'>
                                          <Badge
                                            variant={getPriorityColor(qual.priority)}
                                            className='text-xs'
                                          >
                                            {qual.priority}
                                          </Badge>
                                          {/* Only show required counts and renewal for courses, not checklists or documentation */}
                                          {qual.requirement_type !== 'internal_checklist' && qual.requirement_type !== 'documentation' && (qual.calculated_required || qual.minimum_personnel) && (
                                            <Badge variant='secondary' className='text-xs'>
                                              <Users className='h-3 w-3 mr-1' />
                                              {getRequiredCount(qual)}{' '}
                                              {getRequiredCount(qual) === 1 ? 'person' : 'people'}
                                            </Badge>
                                          )}
                                          {qual.requirement_type !== 'internal_checklist' && qual.requirement_type !== 'documentation' && qual.recommended_required && (
                                            <Badge variant='outline' className='text-xs bg-blue-50 text-blue-700 border-blue-200'>
                                              <Users className='h-3 w-3 mr-1' />
                                              {qual.recommended_required}{' '}
                                              {qual.recommended_required === 1 ? 'person' : 'people'} rec.
                                            </Badge>
                                          )}
                                          {qual.requirement_type !== 'internal_checklist' && qual.requirement_type !== 'documentation' && qual.renewal_period && (
                                            <Badge variant='outline' className='text-xs'>
                                              <Clock className='h-3 w-3 mr-1' />
                                              {qual.renewal_period}
                                            </Badge>
                                          )}
                                          {qual.legal_requirement && (
                                            <Badge variant='outline' className='text-xs border-amber-300 text-amber-700 bg-amber-50'>
                                              <Shield className='h-3 w-3 mr-1' />
                                              Legal
                                            </Badge>
                                          )}
                                          {getStatusIcon(qual.status)}
                                        </div>

                                        {/* Regulatory info */}
                                        {qual.regulatory_body && (
                                          <div className='mt-2 ml-6 text-xs text-gray-500'>
                                            {qual.regulatory_body}
                                            {qual.regulation_reference && ` • ${qual.regulation_reference}`}
                                          </div>
                                        )}

                                        {/* Actions row with approval buttons */}
                                        <div className='mt-3 ml-6 flex items-center justify-between gap-2'>
                                          {/* Left side - type-specific actions (only when approved) */}
                                          <div className='flex items-center gap-2'>
                                            {qual.approval_status === 'approved' && (
                                              <>
                                                {/* Checklist actions */}
                                                {qual.requirement_type === 'internal_checklist' && (
                                                  <>
                                                    <Button
                                                      variant='outline'
                                                      size='sm'
                                                      className='h-7 text-xs'
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        console.log('Link checklist:', qual.id);
                                                      }}
                                                    >
                                                      Link
                                                    </Button>
                                                    <Button
                                                      variant='default'
                                                      size='sm'
                                                      className='h-7 text-xs'
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        console.log('Create checklist:', qual.id);
                                                      }}
                                                    >
                                                      Create
                                                    </Button>
                                                  </>
                                                )}

                                                {/* Documentation actions */}
                                                {qual.requirement_type === 'documentation' && (
                                                  <>
                                                    <Button
                                                      variant='outline'
                                                      size='sm'
                                                      className='h-7 text-xs'
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        console.log('Link document:', qual.id);
                                                      }}
                                                    >
                                                      <Link className='h-3 w-3 mr-1' />
                                                      Link
                                                    </Button>
                                                    <Button
                                                      variant='outline'
                                                      size='sm'
                                                      className='h-7 text-xs'
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        console.log('Upload document:', qual.id);
                                                      }}
                                                    >
                                                      <Upload className='h-3 w-3 mr-1' />
                                                      Upload
                                                    </Button>
                                                  </>
                                                )}

                                                {/* Course actions */}
                                                {qual.requirement_type !== 'internal_checklist' && qual.requirement_type !== 'documentation' && (
                                                  <>
                                                    {qual.recommendations_status === 'generating' ? (
                                                      <Button
                                                        variant='ghost'
                                                        size='sm'
                                                        className='h-7 text-xs text-gray-600'
                                                        disabled
                                                      >
                                                        <Loader2 className='h-3 w-3 mr-2 animate-spin' />
                                                        AI Generating...
                                                      </Button>
                                                    ) : qual.recommendations_status === 'completed' ? (
                                                      <>
                                                        <Button
                                                          variant='ghost'
                                                          size='sm'
                                                          className='h-7 text-xs'
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleGenerateAssignments(qual);
                                                          }}
                                                        >
                                                          View Recommendations
                                                        </Button>
                                                      </>
                                                    ) : qual.recommendations_status === 'error' ? (
                                                      <Button
                                                        variant='ghost'
                                                        size='sm'
                                                        className='h-7 text-xs text-red-500 hover:text-red-600 hover:bg-red-50'
                                                        onClick={e => {
                                                          e.stopPropagation();
                                                          handleRetryRecommendation(qual.id, qual.name);
                                                        }}
                                                      >
                                                        <AlertCircle className='h-3 w-3 mr-1' />
                                                        Failed - Retry
                                                      </Button>
                                                    ) : (
                                                      <Button
                                                        variant='ghost'
                                                        size='sm'
                                                        className='h-7 text-xs text-gray-600'
                                                        disabled
                                                      >
                                                        <Clock className='h-3 w-3 mr-1' />
                                                        Pending AI
                                                      </Button>
                                                    )}
                                                  </>
                                                )}
                                              </>
                                            )}
                                          </div>

                                          {/* Right side - approval buttons and status */}
                                          <div className='flex items-center gap-2'>
                                            {/* Info icon with tooltip showing who approved/rejected */}
                                            {(qual.approval_status === 'approved' || qual.approval_status === 'rejected') && qual.approval_data && (
                                              <TooltipProvider>
                                                <Tooltip>
                                                  <TooltipTrigger asChild>
                                                    <div className='h-7 w-7 flex items-center justify-center cursor-help'>
                                                      <Info className='h-4 w-4 text-gray-400' />
                                                    </div>
                                                  </TooltipTrigger>
                                                  <TooltipContent>
                                                    <p className='text-xs'>
                                                      {qual.approval_status === 'approved' ? 'Approved' : 'Rejected'} by {qual.approval_data.approved_by_name || qual.approval_data.rejected_by_name}
                                                    </p>
                                                  </TooltipContent>
                                                </Tooltip>
                                              </TooltipProvider>
                                            )}
                                            <Button
                                              variant={qual.approval_status === 'approved' ? 'default' : 'outline'}
                                              size='sm'
                                              className={cn(
                                                'h-7 text-xs min-w-[90px] justify-center',
                                                qual.approval_status === 'approved'
                                                  ? 'bg-green-600 hover:bg-green-700 text-white'
                                                  : 'text-green-600 border-green-300 hover:bg-green-50 hover:text-green-700'
                                              )}
                                              onClick={e => {
                                                e.stopPropagation();
                                                handleApproveRequirement(qual.id);
                                              }}
                                            >
                                              <CheckCircle className='h-3 w-3 mr-1' />
                                              {qual.approval_status === 'approved' ? 'Approved' : 'Approve'}
                                            </Button>
                                            <Button
                                              variant={qual.approval_status === 'rejected' ? 'default' : 'outline'}
                                              size='sm'
                                              className={cn(
                                                'h-7 text-xs min-w-[85px] justify-center',
                                                qual.approval_status === 'rejected'
                                                  ? 'bg-red-600 hover:bg-red-700 text-white'
                                                  : 'text-red-600 border-red-300 hover:bg-red-50 hover:text-red-700'
                                              )}
                                              onClick={e => {
                                                e.stopPropagation();
                                                handleRejectRequirement(qual.id);
                                              }}
                                            >
                                              <XCircle className='h-3 w-3 mr-1' />
                                              {qual.approval_status === 'rejected' ? 'Rejected' : 'Reject'}
                                            </Button>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </AccordionContent>
                              </AccordionItem>
                            ))}
                          </Accordion>
                        );
                      })()}
                    </div>
                  ) : (
                    /* Progress status */
                    <div className='flex flex-col items-center px-8 py-4'>
                      <div className='w-full max-w-md'>
                        <div className='sticky top-0 z-10 -mt-3 mb-4'>
                          <h3 className='text-sm font-medium text-gray-700 text-center py-3'>
                            Setup Progress
                          </h3>
                          <div className='absolute inset-0 bg-gradient-to-b from-gray-50 via-gray-50/90 to-transparent pointer-events-none' style={{ zIndex: -1 }} />
                        </div>
                        <div className='space-y-4'>
                          {[
                            {
                              title: 'Company Information',
                              description:
                                'Company name, industry, and location',
                              status:
                                (companyName || space?.name) && (industry || space?.industry)
                                  ? 'complete'
                                  : 'incomplete',
                              icon: Building,
                              fields: [
                                (companyName || space?.name) && `✓ ${companyName || space?.name}`,
                                (industry || space?.industry) && `✓ ${industry || space?.industry}`,
                                (location || space?.location) && `✓ ${location || space?.location}`
                              ].filter(Boolean)
                            },
                            {
                              title: 'Team Size',
                              description: 'Number of employees',
                              status: (teamSize || teamMembers?.length) ? 'complete' : 'incomplete',
                              icon: Users,
                              fields: (teamSize || teamMembers?.length)
                                ? [
                                    `✓ ${teamSize || teamMembers?.length} ${
                                      (teamSize || teamMembers?.length) === 1 ? 'person' : 'people'
                                    }`
                                  ]
                                : []
                            },
                            {
                              title: 'Project Details',
                              description: 'Project description and requirements',
                              status:
                                projectDescription || space?.description || specialRequirements ? 'complete' : 'incomplete',
                              icon: FileText,
                              fields: [
                                (projectDescription || space?.description) && `✓ Description: ${(projectDescription || space?.description).length > 50 ? (projectDescription || space?.description).substring(0, 50) + '...' : (projectDescription || space?.description)}`,
                                specialRequirements && `✓ Special Requirements: ${specialRequirements.length > 50 ? specialRequirements.substring(0, 50) + '...' : specialRequirements}`
                              ].filter(Boolean)
                            },
                            {
                              title: 'Risk Assessment',
                              description: 'Equipment and potential risks',
                              status:
                                risks || equipment ? 'complete' : 'incomplete',
                              icon: Shield,
                              fields: [
                                equipment && `✓ Equipment: ${equipment}`,
                                risks && `✓ Risks: ${risks}`
                              ].filter(Boolean)
                            },
                            {
                              title: 'Work Environment',
                              description: 'Work setting, environment, and heights',
                              status:
                                workSetting || environment || heights ? 'complete' : 'incomplete',
                              icon: MapPin,
                              fields: [
                                workSetting && `✓ Work Setting: ${workSetting}`,
                                environment && `✓ Environment: ${environment}`,
                                heights && `✓ Heights Work: ${heights}`
                              ].filter(Boolean)
                            },
                            {
                              title: 'Worker Details',
                              description: 'Worker roles and hazardous substances',
                              status:
                                workerRoles || hazardousSubstances ? 'complete' : 'incomplete',
                              icon: HardHat,
                              fields: [
                                workerRoles && `✓ Roles: ${Array.isArray(workerRoles) ? workerRoles.join(', ') : workerRoles}`,
                                hazardousSubstances && `✓ Hazardous Substances: ${hazardousSubstances}`
                              ].filter(Boolean)
                            },
                            {
                              title: 'Accreditations',
                              description: 'Accreditation and compliance certifications',
                              status:
                                accreditations ? 'complete' : 'incomplete',
                              icon: Award,
                              fields: [
                                accreditations && `✓ ${accreditations}`
                              ].filter(Boolean)
                            },
                            {
                              title: 'Requirements',
                              description: 'Generate compliance requirements',
                              status:
                                qualifications.length > 0
                                  ? 'complete'
                                  : 'incomplete',
                              icon: CheckCircle,
                              fields:
                                qualifications.length > 0
                                  ? [
                                      `✓ ${qualifications.length} requirements generated`
                                    ]
                                  : []
                            }
                          ].map((item, index, arr) => {
                            const isComplete = item.status === 'complete';
                            const Icon = item.icon;

                            return (
                              <div
                                key={index}
                                className='flex items-start gap-3'
                              >
                                <div className='relative h-full'>
                                  <div
                                    className={cn(
                                      'w-10 h-10 rounded-full flex items-center justify-center transition-colors',
                                      isComplete && 'bg-purple-600',
                                      !isComplete && 'bg-gray-100'
                                    )}
                                  >
                                    {isComplete ? (
                                      <Check className='w-5 h-5 text-white' />
                                    ) : (
                                      <Icon className='w-5 h-5 text-gray-400' />
                                    )}
                                  </div>
                                  {index < arr.length - 1 && (
                                    <div
                                      className={cn(
                                        'absolute top-10 bottom-0 left-1/2 -translate-x-1/2 w-0.5',
                                        isComplete
                                          ? 'bg-purple-200'
                                          : 'bg-gray-200'
                                      )}
                                    />
                                  )}
                                </div>
                                <div className='flex-1 pb-4'>
                                  <p
                                    className={cn(
                                      'text-sm font-medium',
                                      isComplete
                                        ? 'text-gray-900'
                                        : 'text-gray-400'
                                    )}
                                  >
                                    {item.title}
                                  </p>
                                  <p className='text-xs text-gray-500 mt-0.5'>
                                    {item.description}
                                  </p>
                                  {item.fields.length > 0 && (
                                    <div className='mt-2 space-y-1'>
                                      {item.fields.map((field, i) => (
                                        <p
                                          key={i}
                                          className='text-xs text-gray-600'
                                        >
                                          {field}
                                        </p>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Progress percentage */}
                        <div className='mt-8'>
                          <div className='flex items-center justify-between text-xs text-gray-500 mb-2'>
                            <span>Overall Progress</span>
                            <span>
                              {Math.round(
                                ([
                                  (companyName || space?.name) && (industry || space?.industry),
                                  teamSize || teamMembers?.length,
                                  risks || equipment,
                                  qualifications.length > 0
                                ].filter(Boolean).length /
                                  4) *
                                  100
                              )}
                              %
                            </span>
                          </div>
                          <div className='w-full bg-gray-200 rounded-full h-2'>
                            <div
                              className='bg-purple-600 h-2 rounded-full transition-all duration-300'
                              style={{
                                width: `${
                                  ([
                                    (companyName || space?.name) && (industry || space?.industry),
                                    teamSize || teamMembers?.length,
                                    risks || equipment,
                                    qualifications.length > 0
                                  ].filter(Boolean).length /
                                    4) *
                                  100
                                }%`
                              }}
                            />
                          </div>
                        </div>

                        {/* Generate Requirements Button */}
                        <div className='mt-8'>
                          <Button
                            onClick={() => generateRequirementsFromWizard()}
                            disabled={isGenerating || !((companyName || space?.name) && (industry || space?.industry) && (teamSize || teamMembers?.length))}
                            className='w-full bg-purple-600 hover:bg-purple-700'
                          >
                            {isGenerating ? (
                              <>
                                <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                                Generating...
                              </>
                            ) : (
                              <>
                                <Wand2 className='h-4 w-4 mr-2' />
                                Generate Requirements
                              </>
                            )}
                          </Button>
                          <p className='text-xs text-gray-500 text-center mt-3'>
                            Or chat to collect more details first
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Powered by Aiflows branding */}
                <div className='absolute bottom-3 left-0 right-0 flex items-center justify-center'>
                  <div className='flex items-center gap-1 text-[11px] text-gray-500 bg-white/80 backdrop-blur-sm px-3 py-1.5 rounded-full shadow-sm border border-gray-200/50'>
                    <span>Powered by</span>
                    <img
                      src='/aiflowslogo.png'
                      alt='Aiflows'
                      className='h-3.5 w-3.5 object-contain -mx-0.5'
                    />
                    <span className='font-medium'>Aiflows</span>
                  </div>
                </div>
              </div>

              {/* Panel 3 - Recommendations (Right - slides in from off-screen) */}
              <div className='w-1/3 bg-gray-50 flex flex-col overflow-hidden relative'>
                <RecommendationsPanel
                  requirement={selectedRequirementForAssignment}
                  recommendations={assignmentRecommendations}
                  allMembers={recommendationMembers}
                  currentAssignments={currentAssignments}
                  companyWideTrainedMembers={companyWideTrainedMembers}
                  companyWideAllMembers={companyWideAllMembers}
                  memberQualifications={memberQualifications}
                  spaceRequirements={qualifications}
                  isLoading={isGeneratingAssignments}
                  error={assignmentRecommendations?.error ? assignmentRecommendations : null}
                  onAssign={handleAssignFromRecommendation}
                  onUnassign={handleUnassignFromRecommendation}
                  onAssignCompanyWide={handleAssignCompanyWide}
                  onAssignMultiple={(ids) => {
                    ids.forEach(id => {
                      const spaceMember = recommendationMembers.find(m => m.id === id);
                      const companyTrainedMember = companyWideTrainedMembers.find(m => m.employeeId === id);
                      const companyAllMember = companyWideAllMembers.find(m => m.employeeId === id);
                      const member = spaceMember || companyTrainedMember || companyAllMember;
                      if (member) {
                        // If it's a company member (not in space), use handleAssignCompanyWide
                        if (!spaceMember && (companyTrainedMember || companyAllMember)) {
                          handleAssignCompanyWide(id, member.name, member);
                        } else {
                          handleAssignFromRecommendation(id, member.name);
                        }
                      }
                    });
                  }}
                  onRetry={() => handleGenerateAssignments(selectedRequirementForAssignment)}
                  onClose={() => setShowAssignmentDialog(false)}
                />
              </div>
            </div>
          </div>

          {/* Edit Requirement Dialog */}
          <Dialog
            open={!!editingRequirement}
            onOpenChange={open => {
              if (!open) {
                setEditingRequirement(null);
                setEditFormData({});
              }
            }}
          >
            <DialogContent className='max-w-2xl max-h-[90vh] overflow-hidden p-0'>
              <div className='overflow-y-auto max-h-[90vh] p-6'>
                <DialogHeader>
                  <DialogTitle>Edit Requirement</DialogTitle>
                  <DialogDescription>
                    Modify the compliance requirement details below.
                  </DialogDescription>
                </DialogHeader>

                <div className='space-y-4 py-4'>
                  <div className='space-y-2'>
                    <Label htmlFor='edit-name'>Requirement Name</Label>
                    <Input
                      id='edit-name'
                      value={editFormData.name || ''}
                      onChange={e =>
                        setEditFormData(prev => ({
                          ...prev,
                          name: e.target.value
                        }))
                      }
                      placeholder='e.g., Forklift Operator License'
                    />
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor='edit-description'>Description</Label>
                    <Textarea
                      id='edit-description'
                      value={editFormData.description || ''}
                      onChange={e =>
                        setEditFormData(prev => ({
                          ...prev,
                          description: e.target.value
                        }))
                      }
                      placeholder='Detailed description of the requirement'
                      rows={3}
                    />
                  </div>

                  <div className='grid grid-cols-2 gap-4'>
                    <div className='space-y-2'>
                      <Label htmlFor='edit-category'>Category</Label>
                      <Select
                        value={editFormData.category || 'Training'}
                        onValueChange={value =>
                          setEditFormData(prev => ({
                            ...prev,
                            category: value
                          }))
                        }
                      >
                        <SelectTrigger id='edit-category'>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value='Equipment'>Equipment</SelectItem>
                          <SelectItem value='Safety'>Safety</SelectItem>
                          <SelectItem value='Compliance'>Compliance</SelectItem>
                          <SelectItem value='Training'>Training</SelectItem>
                          <SelectItem value='Certification'>
                            Certification
                          </SelectItem>
                          <SelectItem value='Environmental'>
                            Environmental
                          </SelectItem>
                          <SelectItem value='Quality'>Quality</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className='space-y-2'>
                      <Label htmlFor='edit-priority'>Priority</Label>
                      <Select
                        value={editFormData.priority || 'medium'}
                        onValueChange={value =>
                          setEditFormData(prev => ({
                            ...prev,
                            priority: value
                          }))
                        }
                      >
                        <SelectTrigger id='edit-priority'>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value='high'>High</SelectItem>
                          <SelectItem value='medium'>Medium</SelectItem>
                          <SelectItem value='low'>Low</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className='grid grid-cols-2 gap-4'>
                    <div className='space-y-2'>
                      <Label htmlFor='edit-renewal'>Renewal Period</Label>
                      <Input
                        id='edit-renewal'
                        value={editFormData.renewal_period || ''}
                        onChange={e =>
                          setEditFormData(prev => ({
                            ...prev,
                            renewal_period: e.target.value
                          }))
                        }
                        placeholder='e.g., 3 years, 12 months'
                      />
                    </div>

                    <div className='space-y-2'>
                      <Label htmlFor='edit-status'>Status</Label>
                      <Select
                        value={editFormData.status || 'pending'}
                        onValueChange={value =>
                          setEditFormData(prev => ({ ...prev, status: value }))
                        }
                      >
                        <SelectTrigger id='edit-status'>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value='active'>Active</SelectItem>
                          <SelectItem value='pending'>Pending</SelectItem>
                          <SelectItem value='expired'>Expired</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor='edit-regulatory'>Regulatory Body</Label>
                    <Input
                      id='edit-regulatory'
                      value={editFormData.regulatory_body || ''}
                      onChange={e =>
                        setEditFormData(prev => ({
                          ...prev,
                          regulatory_body: e.target.value
                        }))
                      }
                      placeholder='e.g., HSE, OSHA, ISO'
                    />
                  </div>

                  <div className='grid grid-cols-2 gap-4'>
                    <div className='space-y-2'>
                      <Label htmlFor='edit-minimum'>Minimum Personnel</Label>
                      <Input
                        id='edit-minimum'
                        value={editFormData.minimum_personnel || ''}
                        onChange={e =>
                          setEditFormData(prev => ({
                            ...prev,
                            minimum_personnel: e.target.value
                          }))
                        }
                        placeholder='e.g., 2, 10%, 1 per 50'
                      />
                    </div>

                    <div className='space-y-2'>
                      <Label htmlFor='edit-calculated'>Required Number</Label>
                      <Input
                        id='edit-calculated'
                        type='number'
                        value={editFormData.calculated_required || ''}
                        onChange={e =>
                          setEditFormData(prev => ({
                            ...prev,
                            calculated_required:
                              parseInt(e.target.value) || null
                          }))
                        }
                        placeholder='Actual number needed'
                      />
                    </div>
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor='edit-regulation'>
                      Regulation Reference
                    </Label>
                    <Input
                      id='edit-regulation'
                      value={editFormData.regulation_reference || ''}
                      onChange={e =>
                        setEditFormData(prev => ({
                          ...prev,
                          regulation_reference: e.target.value
                        }))
                      }
                      placeholder='e.g., PUWER 1998, CDM 2015'
                    />
                  </div>

                  <div className='flex items-center space-x-2'>
                    <input
                      type='checkbox'
                      id='edit-legal'
                      checked={editFormData.legal_requirement || false}
                      onChange={e =>
                        setEditFormData(prev => ({
                          ...prev,
                          legal_requirement: e.target.checked
                        }))
                      }
                      className='h-4 w-4 rounded border-gray-300'
                    />
                    <Label htmlFor='edit-legal' className='font-normal'>
                      This is a legal requirement
                    </Label>
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor='edit-notes'>Additional Notes</Label>
                    <Textarea
                      id='edit-notes'
                      value={editFormData.notes || ''}
                      onChange={e =>
                        setEditFormData(prev => ({
                          ...prev,
                          notes: e.target.value
                        }))
                      }
                      placeholder='Any additional information or context'
                      rows={3}
                    />
                  </div>
                </div>

                <DialogFooter>
                  <Button
                    variant='outline'
                    onClick={() => {
                      setEditingRequirement(null);
                      setEditFormData({});
                    }}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleSaveEdit} disabled={isSaving}>
                    {isSaving ? (
                      <>
                        <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                        Saving...
                      </>
                    ) : (
                      <>Save Changes</>
                    )}
                  </Button>
                </DialogFooter>
              </div>
            </DialogContent>
          </Dialog>

          {/* Clear All Confirmation Dialog */}
          <AlertDialog open={showClearConfirmDialog} onOpenChange={setShowClearConfirmDialog}>
            <AlertDialogContent className="sm:max-w-md p-0 overflow-hidden">
              <div className="p-6 space-y-4">
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-red-100">
                    <AlertTriangle className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="flex-1 space-y-1">
                    <AlertDialogTitle className="text-lg font-semibold">Clear All Requirements</AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone.
                    </AlertDialogDescription>
                  </div>
                </div>
                <div className="rounded-lg bg-muted p-4">
                  <p className="text-sm text-muted-foreground">
                    Are you sure you want to clear all <span className="font-medium text-foreground">{qualifications.length}</span> requirements from this space?
                  </p>
                </div>
                <AlertDialogFooter className="gap-3 pt-2">
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={confirmClearAllQualifications}
                    className='bg-red-600 hover:bg-red-700 focus:ring-red-600'
                  >
                    Clear All
                  </AlertDialogAction>
                </AlertDialogFooter>
              </div>
            </AlertDialogContent>
          </AlertDialog>

          {/* Delete Single Requirement Confirmation Dialog */}
          <AlertDialog open={!!deleteRequirementId} onOpenChange={(open) => !open && setDeleteRequirementId(null)}>
            <AlertDialogContent className="sm:max-w-md p-0 overflow-hidden">
              <div className="p-6 space-y-4">
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-red-100">
                    <AlertTriangle className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="flex-1 space-y-1">
                    <AlertDialogTitle className="text-lg font-semibold">Delete Requirement</AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone.
                    </AlertDialogDescription>
                  </div>
                </div>
                <div className="rounded-lg bg-muted p-4">
                  <p className="text-sm text-muted-foreground">
                    Are you sure you want to delete <span className="font-medium text-foreground">{qualifications.find(q => q.id === deleteRequirementId)?.name || 'this requirement'}</span>?
                  </p>
                </div>
                <AlertDialogFooter className="gap-3 pt-2">
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => {
                      handleDeleteRequirement(deleteRequirementId);
                      setDeleteRequirementId(null);
                    }}
                    className='bg-red-600 hover:bg-red-700 focus:ring-red-600'
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </div>
            </AlertDialogContent>
          </AlertDialog>
        </>
      </AppShell3>
    </PageTransition>
  );
};

export default CompliancePage;
